<template>
  <div class="bookings-list-page">
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <append-mode-banner />

    <!--    Add booking-->
    <div class="d-lg-block d-none">
      <div class="row">
        <div class="col">
          <div class="mt-3 " style="max-width: 200px"
               v-if="(hasPermissions('bookings_create') && hasPermissions('booking_slot_update')) || hasPermissions('resource_slots_update')">
            <base-dropdown
              title-classes="dropdown-toggle btn btn-primary btn-block"
              title="Create new"
            >
              <a class="dropdown-item" v-if="hasPermissions('booking_slot_update')" @click="newBookingSlot($event)" href="#">Custom time slot</a>
              <a class="dropdown-item" v-if="hasPermissions('resource_slots_update')" @click="newResourceSlot($event)" href="#">Custom resource slot</a>
            </base-dropdown>
          </div>
        </div>
        <div class="d-flex align-items-center" style="margin-top: 16px;">
          <base-button v-if="getCart() > 0" style="height: 38px;" simple class="mr-2" type="primary" v-on:click="goToCart()">
            <i class="tim-icons icon-cart"></i>
            Cart ({{getCart()}})</base-button>

          <!-- View switcher -->
          <div class="view-switcher mr-2">
            <el-tooltip content="List view" placement="bottom">
              <button
                class="view-switcher__btn view-switcher__btn--active"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </el-tooltip>
            <el-tooltip content="Event calendar" placement="bottom">
              <button
                class="view-switcher__btn"
                @click="goToCalendar()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            </el-tooltip>
            <el-tooltip content="Resource calendar (Beta)" placement="bottom">
              <button
                class="view-switcher__btn"
                @click="goToResourceCalendar()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </el-tooltip>
            <el-tooltip content="Kanban board" placement="bottom">
              <button
                class="view-switcher__btn"
                @click="goToKanban()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
              </button>
            </el-tooltip>
          </div>

          <gear-menu class="mr-2" @command="handleGearCommand">
            <el-dropdown-item command="bulk-actions" v-if="hasPermissions('booking_slot_update')">
              <i class="tim-icons icon-bullet-list-67"></i> Bulk actions
            </el-dropdown-item>
            <el-dropdown-item command="booking-slot-settings" v-if="hasPermissions('booking_slot_update')">
              <b-icon icon="card-list" scale="1"></b-icon> Booking slot settings
            </el-dropdown-item>
            <el-dropdown-item command="workflow-states" v-if="hasPermissions('booking_slot_update')">
              <i class="tim-icons icon-components"></i> Workflow states
            </el-dropdown-item>
            <el-dropdown-item command="calendar-settings" v-if="can_edit_calendar_policy">
              <i class="tim-icons icon-settings-gear-63"></i> View settings
            </el-dropdown-item>
            <el-dropdown-item command="hide-schedule-name" v-if="show_schedule_name">
              <i class="tim-icons icon-tag"></i> Hide schedule name
            </el-dropdown-item>
            <el-dropdown-item command="show-schedule-name" v-if="!show_schedule_name">
              <i class="tim-icons icon-tag"></i> Show schedule name
            </el-dropdown-item>
            <el-dropdown-item command="show-staff" v-if="!staff_settings && show_staff_settings">
              <i class="tim-icons el-icon-user-solid"></i> Show staff
            </el-dropdown-item>
            <el-dropdown-item command="hide-staff" v-if="staff_settings && show_staff_settings">
              <i class="tim-icons el-icon-user-solid"></i> Hide staff
            </el-dropdown-item>
          </gear-menu>

          <base-range-selector class="mr-2" :value="range" :options="rangeOptions" label="Range" @input="onRangeChange" />
          <base-date-navigator
            v-if="range === 'day' || range === 'week'"
            class="mr-2"
            style="width: 300px;"
            :key="j"
            :mode="range === 'week' ? 'week' : 'day'"
            :value="currentIso"
            :format="day_of_week + date_format_cal"
            @change="onNavDate"
          />
          <base-date-range-picker
            v-else-if="range === 'custom'"
            class="mr-2"
            :value="customRange"
            :max-days="MAX_RANGE_DAYS"
            @input="onCustomRangeChange"
          />
        </div>
      </div>
    </div>

    <div class="row d-lg-none">
      <!-- Phone toolbar (shared with the other booking views), below 992px (OTC-2955) -->
      <div class="col-12">
        <booking-mobile-toolbar
          :range="range"
          :range-options="rangeOptions"
          :anchor="currentIso"
          :first-day-of-week="first_day_of_week"
          :custom-range="customRange"
          :filter-count="activeFilterCount"
          :filters-open.sync="mobileFiltersOpen"
          @range="onRangeChange"
          @navigate="onNavDate"
          @custom-range="onCustomRangeChange"
        >
          <template #switcher>
              <!-- View switcher (mobile) -->
              <div class="view-switcher">
                <el-tooltip content="List view" placement="bottom">
                  <button
                    class="view-switcher__btn view-switcher__btn--active"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </el-tooltip>
                <el-tooltip content="Event calendar" placement="bottom">
                  <button
                    class="view-switcher__btn"
                    @click="goToCalendar()"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                </el-tooltip>
                <el-tooltip content="Resource calendar (Beta)" placement="bottom">
                  <button
                    class="view-switcher__btn"
                    @click="goToResourceCalendar()"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </el-tooltip>
                <el-tooltip content="Kanban board" placement="bottom">
                  <button
                    class="view-switcher__btn"
                    @click="goToKanban()"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                  </button>
                </el-tooltip>
              </div>
          </template>
          <template #actions>
              <!-- Create: a quiet "+" square on phones (same menu as desktop "Create new") -->
              <el-dropdown
                v-if="hasPermissions('booking_slot_update') || hasPermissions('resource_slots_update')"
                trigger="click"
                @command="cmd => cmd === 'time-slot' ? newBookingSlot() : newResourceSlot()"
              >
                <button type="button" class="booking-mobile-toolbar__square" aria-label="Create new">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="hasPermissions('booking_slot_update')" command="time-slot">Custom time slot</el-dropdown-item>
                  <el-dropdown-item v-if="hasPermissions('resource_slots_update')" command="resource-slot">Custom resource slot</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <base-button v-if="getCart() > 0" style="height: 38px;" simple class="mr-2" type="primary" v-on:click="goToCart()">
                <i class="tim-icons icon-cart"></i>
                Cart ({{getCart()}})</base-button>
          </template>
          <template #gear>
              <gear-menu @command="handleGearCommand">
                  <el-dropdown-item command="bulk-actions" v-if="hasPermissions('booking_slot_update')">
                    <i class="tim-icons icon-bullet-list-67"></i> Bulk actions
                  </el-dropdown-item>
                  <el-dropdown-item command="booking-slot-settings" v-if="hasPermissions('booking_slot_update')">
                    <b-icon icon="card-list" scale="1"></b-icon> Booking slot settings
                  </el-dropdown-item>
                  <el-dropdown-item command="workflow-states" v-if="hasPermissions('booking_slot_update')">
                    <i class="tim-icons icon-components"></i> Workflow states
                  </el-dropdown-item>
                  <el-dropdown-item command="calendar-settings" v-if="can_edit_calendar_policy">
                    <i class="tim-icons icon-settings-gear-63"></i> View settings
                  </el-dropdown-item>
                  <el-dropdown-item command="hide-schedule-name" v-if="show_schedule_name">
                    <i class="tim-icons icon-tag"></i> Hide schedule name
                  </el-dropdown-item>
                  <el-dropdown-item command="show-schedule-name" v-if="!show_schedule_name">
                    <i class="tim-icons icon-tag"></i> Show schedule name
                  </el-dropdown-item>
                  <el-dropdown-item command="show-staff" v-if="!staff_settings && show_staff_settings">
                    <i class="tim-icons el-icon-user-solid"></i> Show staff
                  </el-dropdown-item>
                  <el-dropdown-item command="hide-staff" v-if="staff_settings && show_staff_settings">
                    <i class="tim-icons el-icon-user-solid"></i> Hide staff
                  </el-dropdown-item>
              </gear-menu>
          </template>
        </booking-mobile-toolbar>
      </div>
    </div>

    <!-- Bulk actions drawer (replaces legacy bulk update modal) -->
    <bulk-actions-drawer
      :show.sync="modals.bulk_update_slots"
      :company-id="companyId"
      :company-group-id="companyGroupId"
      :games="games"
      :initial-date="current"
      :date-format="date_format"
      :date-format-cal="date_format_cal"
      :day-of-week="day_of_week"
      :time-format="time_format"
      :first-day-of-week="first_day_of_week"
      :currency-symbol="currency_symbol"
      @close="modals.bulk_update_slots = false"
      @updated="onBulkActionsUpdated"
    />






    <!--Medium and large screens-->
    <div class="row mt-3">
      <div class="col-12">
        <card style="overflow-x: scroll" class="no-scrollbar">
          <!-- On phones this row opens from the toolbar's filter button (kept mounted for saved views) -->
          <div class="row bookings-filter-row" :class="{ 'bookings-filter-row--phone-open': mobileFiltersOpen }">
            <div class="col">
              <filter-bar
                v-if="gamesLoaded"
                :filters="filterSchema"
                :initial-value="filterValues"
                saved-filters-key="bookings.list"
                @change="onFiltersChanged"
              />
            </div>
          </div>

          <booking-range-banner v-if="range !== 'day'" :label="rangeLabel" @switch-to-day="onRangeChange('day')" />

          <div class="row" v-if="resource_slots.length > 0">
            <div class="col">
              <base-button v-if="show_resources" size="sm" class="hover-link" v-on:click="show_resources = false"
                           link style="padding-left: 0px; padding-top: 0px" simple type="primary">Hide assigned resources
                <i class="tim-icons icon-minimal-down"></i>
              </base-button>
              <base-button v-if="!show_resources" size="sm" class="hover-link" v-on:click="show_resources = true"
                           link style="padding-left: 0px; padding-top: 0px" simple type="primary">Show assigned resources
                <i class="tim-icons icon-minimal-down"></i>
              </base-button>
            </div>
          </div>

          <div class="row mb-2" style="position: sticky; top: 0; padding-top: 20px; background-color: inherit; z-index: 100" v-if="show_resources && resource_slots.length > 0" >
            <div class="col">
              <h4 class="card-title">Resources</h4>
              <b-table striped hover :items="resource_slots"
                       :fields="['resource', 'category', 'start', 'end', 'behavior', 'schedule_name', 'actions']"     >
                <template v-slot:cell(resource)="row">
                  <span v-if="row.item.company_user_id">{{row.item.first_name}} {{row.item.last_name}}</span>
                  <span v-else>{{row.item.label}}</span>
                </template>
                <template v-slot:cell(category)="row">
                  <div v-if="row.item.resource_category_id">
                    <badge :style="{'background-color': row.item.category_color}">{{row.item.category_name}}</badge>
                  </div>
                  <div v-else>
                   -
                  </div>
                </template>
                <template v-slot:cell(start)="row">
                  <span>{{getFormattedDate(row.item.start_date)}}, {{getTime(row.item.start_time)}}</span>
                </template>
                <template v-slot:cell(end)="row">
                  <span>{{getFormattedDate(row.item.end_date)}}, {{getTime(row.item.end_time)}}</span>
                </template>
                <template v-slot:cell(behavior)="row">
                  <badge type="default" v-if="row.item.enable_autoblock && row.item.block_status === 'call_to_book'">Auto Call To Book</badge>
                  <badge type="default" v-if="row.item.enable_autoblock && row.item.block_status === 'blocked'">Auto Block</badge>
                  <badge type="default" v-if="!row.item.enable_autoblock">No action</badge>
                  <badge type="success" v-if="row.item.enable_auto_unblock" class="ml-1">Auto Unblock</badge>
                  <badge type="warning" v-if="row.item.saturate_on_first_assignment" class="ml-1">Locked After Assignment</badge>
                </template>
                <template v-slot:cell(assigned_resources)="row">
                  <span>{{row.item.count}}</span>
                </template>
                <template v-slot:cell(schedule_name)="row">
                  <badge type="default"  v-if="row.item.schedule_id">
                    <i class="tim-icons icon-calendar-60 mr-1"></i> {{row.item.schedule_name}}</badge>
                  <badge type="default" v-else><i class="tim-icons icon-calendar-60 mr-1"></i> Manually created</badge>
                </template>
                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button
                      simple
                      type="primary"
                      link
                    > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a v-if="hasPermissions('resource_slots_update')" class="dropdown-item mt-2 mb-2" @click.prevent="updateResourceSlotPrompt(row.item, $event)" href="#">
                          <i class="tim-icons icon-pencil text-warning mr-2"></i> Edit resource slot
                        </a>
                        <a class="dropdown-item mb-2" href="#" @click="viewResourceActivityLog(row.item, $event)">
                          <i class="tim-icons text-info el-icon-notebook-2 mr-2"></i> View activity log
                        </a>
                        <hr v-if="hasPermissions('resource_slots_delete')">
                        <a v-if="hasPermissions('resource_slots_delete')" class="dropdown-item mt-2 mb-2" @click="deleteResourceSlotPrompt(row.item, $event, 'menu')" href="#">
                          <i class="tim-icons icon-trash-simple text-danger mr-2"></i> Delete resource slot
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
              <hr style="border-top: 1px solid #f4f5f7;">
            </div>
          </div>

          <!--   No data panel -->
          <div v-if="bookings.length <= 0" class="bookings-empty-state mt-4">
            <img src="/img/pandas/empty-bookings-list.png" alt="No bookings" class="empty-state-img" />
            <h4 class="empty-state-text">{{ range === 'day' ? 'No bookings on this date' : 'No booked slots in this date range' }}</h4>
            <p class="bookings-empty-state__hint">
              Once customers book or you add slots, they'll show up here.
            </p>
          </div>

          <!-- Bookings table (card-styled rows for column alignment) -->
          <div v-if="bookings.length > 0" class="bookings-table-wrap">
          <table class="bookings-table">
            <colgroup>
              <col style="width: 100px;" />
              <col :style="{ width: statusColWidth + 'px' }" />
              <col />
              <col />
              <col style="width: 110px;" />
              <col style="width: 140px;" />
              <col style="width: 180px;" />
              <col style="width: 180px;" />
            </colgroup>
            <thead>
              <tr>
                <th>Time</th>
                <th>Status</th>
                <th>Event</th>
                <th>Customer</th>
                <th>Participants</th>
                <th>Payments</th>
                <th>Notes</th>
                <th>Staff</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in tableEntries">
              <tr v-if="item.__dayHeader" :key="'DH' + item.date" class="bookings-day-header">
                <td colspan="8" class="text-white"><button type="button" class="bookings-day-header__link" title="Open in day view" @click="onRangeOpenDay(item.date)">{{ item.label }}</button> <span class="bookings-day-header__count">({{ item.count }})</span></td>
              </tr>
              <tr
                v-else
                :key="'BK' + (item.__groupDate || '') + item.id"
                class="bookings-row"
                :class="{ 'bookings-row--available': item.status === 'available' }"
                :style="{ '--booking-card-accent': getStatusDisplay(item).color }"
                @click="handleBookingRowClick(item)"
              >
                <td class="bookings-row__time" data-label="Time">
                  <span v-if="!item.is_all_day" class="booking-card__time-text">{{ getTime(item.start_time) }}</span>
                  <span v-else class="booking-card__time-allday">{{ item.all_day_text }}</span>
                </td>

                <td data-label="Status">
                  <el-dropdown v-if="isStatusEditable(item)" trigger="click" :popper-append-to-body="true" @command="(cmd) => quickSetStatus(item, cmd)" @click.native.stop>
                    <span class="booking-status-pill booking-status-pill--editable" :class="{ 'booking-status-pill--available': item.status === 'available' }" :style="{ backgroundColor: getStatusDisplay(item).color, color: getStatusDisplay(item).text_color }" :title="getStatusDisplay(item).label">
                      <span class="booking-status-pill__label">{{ getStatusDisplay(item).label }}</span>
                      <i class="el-icon-arrow-down booking-status-pill__caret"></i>
                    </span>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item
                          v-for="opt in editableStatusOptions"
                          :key="opt.status"
                          :command="opt.status"
                          :disabled="opt.status === item.status"
                        >
                          <span class="booking-status-pill__dot mr-1" :style="{ backgroundColor: opt.color }"></span>
                          {{ opt.label }}
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                  <span v-else class="booking-status-pill" :class="{ 'booking-status-pill--available': item.status === 'available' }" :style="{ backgroundColor: getStatusDisplay(item).color, color: getStatusDisplay(item).text_color }" :title="getStatusDisplay(item).label">
                    <span class="booking-status-pill__label">{{ getStatusDisplay(item).label }}</span>
                  </span>
                  <span
                    v-if="getSlotNotice(item)"
                    class="slot-notice"
                    :class="getSlotNotice(item).state === 'closed' ? 'slot-notice--closed' : 'slot-notice--countdown'"
                    :title="getSlotNotice(item).title"
                  >
                    <i class="tim-icons" :class="getSlotNotice(item).state === 'closed' ? 'icon-lock-circle' : 'icon-time-alarm'"></i>{{ getSlotNotice(item).label }}
                  </span>
                  <span v-if="getNotesCount(item) > 0" class="booking-card__notes-chip" @click.stop="openBookingDrawerForRow(item, 'notes')">
                    <i class="el-icon-document booking-card__notes-chip-icon"></i>
                    {{ getNotesCount(item) }} {{ getNotesCount(item) === 1 ? 'note' : 'notes' }}
                  </span>
                </td>

                <td data-label="Event">
                  <div class="bookings-row__event">
                    <img v-if="item.url" v-twin-src="item.url" class="booking-avatar" alt="" />
                    <div class="booking-card__event-info">
                      <span class="booking-card__game">{{ item.game_name }}</span>
                      <div class="booking-card__event-meta">
                        <span v-if="show_schedule_name" class="booking-card__schedule">
                          <i class="tim-icons icon-calendar-60"></i>
                          <span>{{ item.schedule_id ? item.schedule_name : 'Manually created' }}</span>
                        </span>
                        <el-dropdown v-if="getWorkflowState(item) && workflowStates.length > 0" trigger="click" @command="(cmd) => quickSetWorkflowState(item, cmd)" @click.native.stop>
                          <div class="workflow-badge" :style="getWorkflowBadgeStyle(item)">
                            <b-icon v-if="getWorkflowState(item).icon" :icon="getWorkflowState(item).icon" style="font-size: 9px;"></b-icon>
                            <span>{{ getWorkflowState(item).name }}</span>
                          </div>
                          <template #dropdown>
                            <el-dropdown-menu>
                              <el-dropdown-item
                                v-for="state in workflowStates"
                                :key="state.id"
                                :command="state.id"
                                :disabled="state.id === item.workflow_state_id"
                              >
                                <b-icon v-if="state.icon" :icon="state.icon" class="mr-1" :style="{ color: state.color }"></b-icon>
                                <span v-else class="workflow-dropdown-dot mr-1" :style="{ backgroundColor: state.color }"></span>
                                {{ state.name }}
                              </el-dropdown-item>
                              <el-dropdown-item divided command="clear" v-if="item.workflow_state_id">Clear state</el-dropdown-item>
                            </el-dropdown-menu>
                          </template>
                        </el-dropdown>
                        <tag-dots
                          v-if="item.tags && item.tags.length"
                          :tags="item.tags"
                          @click="openBookingDrawerForRow(item, 'detail')"
                        ></tag-dots>
                        <badge type="danger" v-if="(item.status === 'available' || item.status === 'call_to_book' || item.status === 'requestable') && item.in_cart">In cart</badge>
                        <span v-if="hasAddons(item)" class="booking-alert-chip booking-alert-chip--neutral booking-card__addons-chip booking-card__addons-chip--event" @click.stop="openBookingDrawerForRow(item, 'detail', 'transaction')">
                          <i class="tim-icons icon-bag-16 mr-1"></i>Addons
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                <td :data-label="getCustomerRowKind(item) ? 'Customer' : ''">
                  <template v-if="getCustomerRowKind(item) === 'customer'">
                    <div class="bookings-row__customer">
                      <div class="bookings-row__customer-name-row">
                        <a
                          v-if="hasPermissions('customer_view') && item.end_user_id"
                          :href="'/customers/details?id=' + item.end_user_id"
                          target="_blank"
                          rel="noopener"
                          class="booking-card__customer-link"
                          @click.stop
                        >
                          {{ item.first_name }} {{ item.last_name }}
                          <i class="tim-icons el-icon-top-right booking-card__customer-link-icon"></i>
                        </a>
                        <span v-else class="booking-card__customer-name">{{ item.first_name }} {{ item.last_name }}</span>
                        <span v-if="hasCustomerAlerts(item)" class="booking-alert-chip booking-alert-chip--alert" @click.stop="openBookingDrawerForRow(item, 'detail', 'customer')">
                          <i class="tim-icons icon-alert-circle-exc mr-1"></i>Alert
                        </span>
                      </div>
                      <div v-if="item.email || item.phone" class="bookings-row__customer-contact">
                        <small v-if="item.email" class="booking-card__customer-detail">
                          <i class="tim-icons icon-email-85"></i> {{ item.email }}
                        </small>
                        <small v-if="item.phone" class="booking-card__customer-detail">
                          <i class="tim-icons el-icon-mobile-phone"></i> {{ formatPhone(item.country, item.phone) }}
                        </small>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="getCustomerRowKind(item) === 'incomplete'">
                    <span class="booking-card__customer-name text-warning">
                      <i class="tim-icons icon-settings mr-1"></i>Incomplete
                    </span>
                  </template>
                  <div v-else-if="hasPermissions('bookings_create') && !isBookedOrCompleted(item)" class="bookings-row__inline-actions" @click.stop>
                    <button type="button" class="booking-quick-action booking-quick-action--sm" @click="openBookingDrawerForRow(item, 'create-booking')">
                      <i class="tim-icons icon-simple-add"></i>Book now
                    </button>
                  </div>
                  <span v-else class="booking-card__cell-empty">—</span>
                </td>

                <td data-label="Participants">
                  <div v-if="isBookedOrCompleted(item)" class="booking-card__stat-line">{{ item.group_size }} {{ item.group_size === 1 ? 'participant' : 'participants' }}</div>
                  <!-- Signed waivers show for any slot that has them; a waiver never changes the slot's status (OTC-3117) -->
                  <template v-if="isBookedOrCompleted(item) || getWaiverCount(safeParseWaivers(item)) > 0">
                    <template v-if="item.waivers">
                      <el-popover
                        v-if="getWaiverCount(safeParseWaivers(item)) > 0"
                        placement="bottom-start"
                        trigger="click"
                        popper-class="booking-card__waivers-popover"
                        @show="fetchWaiversForBooking(item)"
                      >
                        <span slot="reference" class="booking-card__stat-line booking-card__stat-line--sub booking-card__checkin-trigger" :class="{ 'booking-card__checkin-trigger--signed': !isBookedOrCompleted(item) }" @click.stop>
                          <i v-if="!isBookedOrCompleted(item)" class="tim-icons icon-paper mr-1"></i>{{ getWaiverCount(safeParseWaivers(item)) }} signed
                          <i class="el-icon-arrow-down booking-card__checkin-caret"></i>
                        </span>
                        <!-- Skeleton while fetching -->
                        <div
                          v-if="!waiversByBooking[item.id] || waiversByBooking[item.id].loading"
                          class="booking-card__waivers-list booking-card__waivers-list--loading"
                        >
                          <div
                            v-for="n in Math.max(1, getWaiverCount(safeParseWaivers(item)))"
                            :key="'sk' + n"
                            class="booking-card__waivers-item"
                          >
                            <span class="skel skel--text" style="width: 60%; height: 14px;"></span>
                            <span class="skel skel--text" style="width: 80%; height: 12px; margin-top: 4px;"></span>
                          </div>
                        </div>
                        <!-- Error state -->
                        <div
                          v-else-if="waiversByBooking[item.id].error"
                          class="booking-card__waivers-empty"
                        >
                          Couldn't load waivers
                        </div>
                        <!-- Loaded list -->
                        <div
                          v-else-if="waiversByBooking[item.id].data && waiversByBooking[item.id].data.length"
                          class="booking-card__waivers-list"
                        >
                          <div
                            v-for="(w, idx) in waiversByBooking[item.id].data"
                            :key="w.waiver_id || w.id || idx"
                            class="booking-card__waivers-item"
                          >
                            <div class="booking-card__waivers-name-row">
                              <a
                                v-if="hasPermissions('customer_view') && (w.end_user_id || w.user_id)"
                                :href="'/customers/details?id=' + (w.end_user_id || w.user_id)"
                                target="_blank"
                                rel="noopener"
                                class="booking-card__waivers-name"
                                @click.stop
                              >
                                {{ getWaiverDisplayName(w) }}
                                <i class="el-icon-top-right"></i>
                              </a>
                              <span v-else class="booking-card__waivers-name">{{ getWaiverDisplayName(w) }}</span>
                              <span v-if="Number(w.is_minor) === 1" class="booking-card__waivers-tag booking-card__waivers-tag--minor">Minor</span>
                              <span v-if="Number(w.not_participating) === 1" class="booking-card__waivers-tag booking-card__waivers-tag--not-participating">Not participating</span>
                            </div>
                            <a
                              v-if="w.email"
                              :href="'mailto:' + w.email"
                              class="booking-card__waivers-email"
                              @click.stop
                            >
                              <i class="tim-icons icon-email-85"></i> {{ w.email }}
                            </a>
                          </div>
                        </div>
                        <div v-else class="booking-card__waivers-empty">
                          No one has signed yet
                        </div>
                      </el-popover>
                      <div v-else class="booking-card__stat-line booking-card__stat-line--sub">0 signed</div>
                    </template>
                  </template>
                  <span v-else class="booking-card__cell-empty">—</span>
                </td>

                <td data-label="Payments">
                  <template v-if="isBookedOrCompleted(item)">
                    <span v-if="getCustomerRowKind(item) === 'public'" class="booking-card__stat-line booking-card__transaction-ref">
                      Public booking ({{ item.public_slots.length }} group<span v-if="item.public_slots.length !== 1">s</span>)
                    </span>
                    <span v-else-if="item.transaction_id" class="booking-card__stat-line booking-card__transaction-ref">
                      Txn
                      <a
                        v-if="hasPermissions('transactions_view')"
                        :href="'/purchases/transactions/details?id=' + item.transaction_id"
                        target="_blank"
                        rel="noopener"
                        class="booking-card__transaction-ref-link"
                        @click.stop
                      >
                        #{{ getOrderNumber(item) }}
                        <i class="tim-icons el-icon-top-right"></i>
                      </a>
                      <span v-else>#{{ getOrderNumber(item) }}</span>
                    </span>
                    <span
                      class="booking-card__stat-line booking-card__amount--due"
                      :class="{ 'booking-card__amount--due--collectable': hasPermissions('payments_update') && (item.transaction_status === 2 || getDue(item) > 0) }"
                    >Due: {{ getAmount(item.transaction_status === 2 ? item.total : getDue(item)) }}</span>
                    <span class="booking-card__stat-line booking-card__amount--paid">Paid: {{ getAmount(item.transaction_status === 2 ? 0 : getPaid(item)) }}</span>
                    <span v-if="hasAddons(item)" class="booking-alert-chip booking-alert-chip--neutral booking-card__addons-chip" @click.stop="openBookingDrawerForRow(item, 'detail', 'transaction')">
                      <i class="tim-icons icon-bag-16 mr-1"></i>Addons
                    </span>
                    <a v-if="hasPermissions('payments_update') && (item.transaction_status === 2 || getDue(item) > 0)" class="bookings-row__collect-link" @click.stop="openBookingDrawerForRow(item, 'collect-payment')">
                      Collect {{ getAmount(item.transaction_status === 2 ? item.total : getDue(item)) }} due
                    </a>
                  </template>
                  <span v-else class="booking-card__cell-empty">—</span>
                </td>

                <td data-label="Notes">
                  <div v-if="getImportantNoteText(item)" class="booking-card__note" @click.stop="openBookingDrawerForRow(item, 'notes')">
                    <span class="booking-card__note-text">{{ getImportantNoteText(item) }}</span>
                    <span v-if="otherNotesCount(item) > 0" class="booking-card__note-more">+{{ otherNotesCount(item) }}</span>
                  </div>
                  <div class="booking-card__notes-actions">
                    <button v-if="hasPermissions('payments_update') && (item.transaction_status === 2 || getDue(item) > 0) && isBookedOrCompleted(item)" type="button" class="booking-quick-action booking-quick-action--sm booking-card__collect-action" @click.stop="openBookingDrawerForRow(item, 'collect-payment')">
                      <i class="tim-icons icon-credit-card"></i>Collect {{ getAmount(item.transaction_status === 2 ? item.total : getDue(item)) }}
                    </button>
                    <span v-if="hasPermissions('bookings_update') || hasPermissions('booking_slot_update') || hasPermissions('booking_notes_update')" class="booking-alert-chip booking-alert-chip--note" @click.stop="openBookingDrawerForRow(item, 'notes')">
                      <i class="tim-icons icon-simple-add mr-1"></i>Add note
                    </span>
                    <button type="button" class="booking-quick-action booking-quick-action--sm booking-card__edit-action" @click.stop="openBookingDrawerForRow(item)">
                      <i class="tim-icons icon-pencil"></i>Edit
                    </button>
                  </div>
                </td>

                <td data-label="Staff" class="bookings-row__staff">
                  <template v-if="item.staff && item.staff.length > 0">
                    <div
                      v-for="(staff, index) in item.staff"
                      :key="'STAFF' + item.id + '-' + index"
                      class="booking-card__stat-line text-white"
                    >
                      <i class="el-icon-user-solid mr-1"></i>{{ staff.first_name }} {{ staff.last_name }}
                    </div>
                  </template>
                  <template v-else>
                    <div class="booking-card__stat-line">
                      <i class="el-icon-user-solid mr-1"></i>{{ getAvailableStaffCount(item).assigned }} staff assigned
                    </div>
                  </template>
                  <div
                    v-for="(line, lineIndex) in resourceAvailabilityLines(item)"
                    :key="'RES' + item.id + '-' + lineIndex"
                    class="booking-card__stat-line booking-card__stat-line--sub"
                  >
                    <i class="tim-icons icon-badge mr-1"></i>{{ line }}
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
          </div>

        </card>
      </div>
    </div>

    <!-- Resource Drawer (create + edit + activity log) -->
    <resource-drawer
      v-if="companyGroupId"
      :show.sync="modals.resourceDrawer"
      :resource-slot="selectedResourceSlot"
      :company-id="companyId"
      :company-group-id="companyGroupId"
      :games="games"
      :block-statuses="block_statuses"
      :priorities="priorities"
      :date-format="date_format"
      :time-format="time_format"
      :first-day-of-week="first_day_of_week"
      :initial-date="current"
      :initial-start-time="resourceDrawerInitialStartTime"
      :initial-end-time="resourceDrawerInitialEndTime"
      :initial-view="resourceDrawerInitialView"
      :validate-delete="validateResourceForDrawer"
      @close="onResourceDrawerClose"
      @resource-changed="onResourceChanged"
    />

    <!-- Booking detail drawer — replaces the legacy edit_booking modal for
         booking row clicks. -->
    <booking-drawer
      :notice-policy="{ show: show_notice_indicators, mode: notice_display_mode, anchor: noticeAnchor }"
      v-if="companyGroupId"
      :show.sync="modals.bookingDrawer"
      :booking="selectedBookingCard"
      :initial-view="drawerInitialView"
      :initial-section="drawerInitialSection"
      :company-id="companyId"
      :company-group-id="companyGroupId"
      :can-edit="hasPermissions('booking_slot_update')"
      :can-cancel="hasPermissions('bookings_cancel')"
      :can-delete-slot="hasPermissions('booking_slot_delete')"
      :can-update-booking="hasPermissions('bookings_update')"
      :can-manage-notes="hasPermissions('booking_notes_update')"
      :can-create-booking="hasPermissions('bookings_create')"
      :can-update-resource-slot="hasPermissions('resource_slots_update')"
      :can-view-project="hasPermissions('project_view')"
      :can-view-customer="hasPermissions('customer_view')"
      :can-view-transaction="hasPermissions('transactions_view')"
      :can-view-game-history="hasPermissions('game_history_view')"
      :can-create-payment="hasPermissions('payments_update')"
      :date-format="date_format"
      :time-format="time_format"
      :currency="currency"
      :currency-symbol="currency_symbol"
      :currency-format="currency_format"
      :number-format="number_format"
      :workflow-states="workflowStates"
      :custom-status-map="customStatusMap"
      :system-status-override-map="systemStatusOverrideMap"
      :slot-statuses="slot_statuses"
      :first-day-of-week="first_day_of_week"
      :show-day-of-week="!!show_day_of_week"
      :custom-statuses="customStatuses"
      :payment-settings="payment_settings"
      :taxes="taxes"
      :fees="fees"
      :country-code="countryCode"
      :is-kanban="false"
      @close="onBookingDrawerClose"
      @booking-changed="onBookingDrawerChanged"
      @workflow-state-changed="onBookingDrawerChanged"
      @add-to-cart="onAddToCartFromDrawer"
    />

    <!-- Create slot drawer — replaces the legacy new_booking_slot modal. -->
    <create-slot-drawer
      v-if="companyGroupId"
      :show.sync="modals.createSlotDrawer"
      :game="createSlotGame"
      :game-id="createSlotGame ? createSlotGame.id : null"
      :initial-date="createSlotDate"
      :initial-start-time="createSlotStartTime"
      :initial-end-time="createSlotEndTime"
      :company-id="companyId"
      :company-group-id="companyGroupId"
      :date-format="date_format"
      :time-format="time_format"
      :first-day-of-week="first_day_of_week"
      :show-day-of-week="!!show_day_of_week"
      :custom-statuses="customStatuses"
      :currency="currency"
      :currency-symbol="currency_symbol"
      @close="onCreateSlotDrawerClose"
      @created="onCreateSlotDrawerCreated"
    />


    <!-- Booking slot settings drawer -->
    <booking-slot-settings-drawer
      :show.sync="modals.bookingSlotSettings"
      @saved="onBookingSlotSettingsSaved"
    />

    <!-- Workflow States Modal -->
    <workflow-states-modal
      :show.sync="modals.workflowStates"
    />

    <!-- Venue view policy (notice indicators etc.), policy-only from the list view -->
    <calendar-settings-drawer
      :show.sync="showCalendarSettingsDrawer"
      :company-group-id="companyGroupId"
      policy-only
      @close="showCalendarSettingsDrawer = false"
      @saved="onCalendarSettingsSaved"
    />

  </div>
</template>

<script>
import { API_LOCATION } from '../../../api-config'
import { captureNoticeAnchor, classifySlotNotice, NOTICE_TICK_MS } from '@/util/slotNotice'
import { canAppendToSlot } from '@/util/appendMode';
import { normalizeStatusFilter } from '@/util/bookingFilters';
import {Auth} from "aws-amplify";
import { BTable } from 'bootstrap-vue'
import {date, time, formattedDate} from '../../../plugins/dateFormatter'
import swal from "sweetalert2";
import {Autocomplete, Dropdown, DropdownItem, DropdownMenu, Popover, Switch, TimePicker} from "element-ui";
import axios from "axios";
import currencies from "@/assets/currencies.json";
import _ from "lodash";
import Badge from "@/components/Badge";
import countries from "@/assets/countries.json";
import moment from 'moment';
import {formatPhoneDisplay} from "@/util/phoneFormatter";
import BookingSlotSettingsDrawer from "./components/BookingSlotSettingsDrawer.vue";
import ResourceDrawer from "./components/ResourceDrawer/ResourceDrawer.vue";
import GearMenu from "@/components/GearMenu.vue";
import CalendarSettingsDrawer from "./components/CalendarSettingsDrawer.vue";
import TagDots from "@/components/TagDots.vue";
import WorkflowStatesModal from "./ResourceCalendar/components/modals/WorkflowStatesModal.vue";
import { BIcon } from 'bootstrap-vue'
import { cacheBookingPageMode, getPreferredBookingsPath, stripViewFilterParams, withDeepLinkBooking } from '@/util/bookingsView'
import { resolveDeepLinkDate, fetchDeepLinkSlot } from '@/services/bookingLookup'
import { getSlotResourceAvailability } from '@/util/resourceAvailability'
import {
  getGames as fetchGames,
  getBookingSlotSettings as fetchSlotSettings,
  getSiteSettings as fetchSiteSettings,
  getWorkflowStates as fetchWorkflowStates,
  getPaymentMethods as fetchPaymentMethods
} from '@/services/groupData'
import BulkActionsDrawer from './components/BulkActionsDrawer.vue'
import BookingDrawer from './components/BookingDrawer/BookingDrawer.vue'
import CreateSlotDrawer from './components/CreateSlotDrawer.vue'
import BookingRangeBanner from './components/BookingRangeBanner.vue'
import BookingMobileToolbar from './components/BookingMobileToolbar.vue'
import {
  MAX_RANGE_DAYS,
  rangeOptions as bookingRangeOptions,
  computeRangeWindow,
  daysInWindow,
  spannedDaysInWindow,
  formatRangeDay,
  formatWindowLabel,
  readRangeState,
  applyRangeToQuery,
  persistRangeState,
  boundaryMinutes,
  operatingWindow,
  extendedMinutes
} from '@/util/bookingRange'

export default {
  name: "Bookings",
  components: {
    BulkActionsDrawer,
    BookingDrawer,
    CreateSlotDrawer,
    Badge,
    BTable,
    [Autocomplete.name]: Autocomplete,
    [TimePicker.name]: TimePicker,
    [Popover.name]: Popover,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Switch.name]: Switch,
    BookingSlotSettingsDrawer,
    ResourceDrawer,
    WorkflowStatesModal,
    BIcon,
    GearMenu,
    CalendarSettingsDrawer,
    TagDots,
    BookingRangeBanner,
    BookingMobileToolbar
  },
  data() {
    return {
      waiversByBooking: {},
      countryCode: JSON.parse(localStorage.getItem('group')).country,
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      companyId: localStorage.getItem('companyId'),
      workflowStates: [],
      enable_utm_source_tracking: 0,
      priorities: [1],
      show_schedule_name: localStorage.getItem('showScheduleNameOnCalendar'),
      slot_statuses: [
        {id: 1, label: 'Available', status: 'available'},
        {id: 2, label: 'Blocked', status: 'blocked'},
        {id: 3, label: 'Call to book', status: 'call_to_book'},
        {id: 4, label: 'Requestable', status: 'requestable'},
      ],
      pricing_types: [],
      show_resources: false,
      resource_slots: [],
      // OTC-3144: active resource category requirements per event id (category events only)
      categoryRequirementsByGame: new Map(),
      // OTC-3144: the group's resource settings the autoblocking module applies
      resourceSettings: { crossGameBuffers: true, requireFullBufferCoverage: false },
      current: '',
      add_payment: false,
      loading: false,
      fields: ["time", "booking", "booked_by", "number_of_players", "paid", "due", "notes", "staff", "actions"],
      bookings: [],
      // Multi-day ranges (OTC-166): Week, Rolling 30 days and Custom list booked slots grouped by day
      range: 'day',
      customRange: [],
      rangeOptions: bookingRangeOptions('list'),
      // Phone toolbar: the filter row opens from the filter button
      mobileFiltersOpen: false,
      MAX_RANGE_DAYS,
      show_day_of_week_group: !!(JSON.parse(localStorage.getItem('group') || '{}').show_day_of_week),
      games: [],
      taxes: [],
      fees: [],
      empty: false,
      // Custom statuses
      customStatuses: [],
      customStatusMap: new Map(),
      systemStatusOverrideMap: new Map(),
      // Hardcoded default colors for built-in statuses
      DEFAULT_STATUS_COLORS: {
        available: { color: '#FFFFFF', text_color: '#1E1E2F' },
        booked: { color: '#1D8CF8', text_color: '#FFFFFF' },
        completed: { color: '#2DCE89', text_color: '#FFFFFF' },
        blocked: { color: '#D44872', text_color: '#FFFFFF' },
        cancelled: { color: '#F5365C', text_color: '#FFFFFF' },
        expired: { color: '#525F7F', text_color: '#FFFFFF' },
        call_to_book: { color: '#FB6340', text_color: '#FFFFFF' },
        requested: { color: '#00D6B4', text_color: '#1E1E2F' },
        requestable: { color: '#FFD600', text_color: '#1E1E2F' }
      },

      modals: {
        bulk_update_slots: false,
        bookingSlotSettings: false,
        workflowStates: false,
        resourceDrawer: false,
        bookingDrawer: false,
        createSlotDrawer: false,
      },
      // BookingDrawer state — replaces the legacy edit_booking modal as the
      // booking detail/edit surface. Legacy modal markup remains for now as
      // a fallback for in-flight sub-flows; new clicks route through here.
      selectedBookingCard: {},
      drawerInitialView: 'detail',
      drawerInitialSection: null,
      show_day_of_week: !!(JSON.parse(localStorage.getItem('group') || '{}').show_day_of_week),
      // CreateSlotDrawer state — replaces the legacy new_booking_slot modal.
      createSlotGame: null,
      createSlotDate: null,
      createSlotStartTime: null,
      createSlotEndTime: null,
      // ResourceDrawer state
      selectedResourceSlot: null,
      resourceDrawerInitialStartTime: null,
      resourceDrawerInitialEndTime: null,
      resourceDrawerInitialView: 'detail',
      // Flat filter values for the shared <filter-bar>: status keys + game ids.
      // Seeded from the URL; saved-views precedence is resolved by FilterBar.
      filterValues: {
        status: [],
        game: [],
        tags: []
      },
      groupTags: [],
      // gamesLoaded is set in mounted() once the URL filter values are seeded, so
      // FilterBar mounts in the same tick and drives the first fetch via its initial
      // `change`. Game options fill in reactively. didInitialFetch preserves that.
      gamesLoaded: false,
      didInitialFetch: false,
      // Set at mount from the ?view= param; keeps getSettings from bouncing an
      // explicit saved-view open to the user's preferred bookings view.
      cameFromSavedView: false,
      deepLinkBookingId: null,
      deepLinkDate: null,
      statuses: ['Available', 'Blocked', 'Call to book', 'Requestable'],
      selectedPaymentOption: {name: '', content: '', id: '', is_default: ''},
      paymentOptions: [],
      card: '',
      square: '',
      squareAppId: '',
      squareLocationId: '',
      payment_settings: {},
      country: '',
      countries: countries.countries,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      // OTC-1586: minutes after midnight where this venue's day starts
      day_boundary: boundaryMinutes(JSON.parse(localStorage.getItem('group') || '{}').day_rollover_time),
      picker_time_format: 'hh:mm A',
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      date_format_cal: '',
      day_of_week: '',
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      j: 0,
      activeTabIndex: 0,
      staff_settings: 0,
      show_notice_indicators: false,
      notice_display_mode: 'indicator',
      noticeAnchor: null,
      noticeNow: 0,
      can_edit_calendar_policy: false,
      showCalendarSettingsDrawer: false,
      show_staff_settings: 0,
      block_statuses: [
        {id: 1, name: 'Blocked', label: 'blocked'},
        {id: 2, name: 'Call to book', label: 'call_to_book'}
      ],
      // WebSocket for real-time booking updates
      ws: null,
      wsReconnectAttempts: 0,
      wsMaxReconnectAttempts: 10,
      wsReconnectDelay: 1000,
      wsHeartbeatInterval: null,
      // WebSocket notification batching
      wsNotifyTimer: null,
      wsRefreshTimer: null,
    }
  },
  computed: {
    // "X/Y resources available" per booking row, computed once per data change
    // instead of on every render (the template reads it up to 3 times per row).
    resourceAvailabilityById() {
      const byId = new Map()
      for (const item of this.bookings || []) {
        byId.set(item.id, getSlotResourceAvailability(item, this.resource_slots, this.categoryRequirementsByGame.get(item.game_id), this.resourceSettings))
      }
      return byId
    },
    // Status column width tracks the widest status pill on the page so custom
    // status names never spill into the image column. Text is measured with
    // the pill's largest font step (11px, weight 500) so the fixed-layout
    // column is never narrower than the pill needs. Clamped: 120px floor keeps
    // the built-in statuses on their settled width, 220px cap hands anything
    // longer to the pill's ellipsis.
    statusColWidth() {
      const PILL_H_PADDING = 20
      const PILL_BORDER = 2
      const CARET = 16   // 10px icon + 6px gap
      const CELL_PADDING = 32
      const MIN = 120
      const MAX = 220
      if (typeof document === 'undefined') return MIN
      if (!this._statusMeasureCtx) {
        this._statusMeasureCtx = document.createElement('canvas').getContext('2d')
      }
      const ctx = this._statusMeasureCtx
      const family = getComputedStyle(document.body).fontFamily || 'Poppins, sans-serif'
      ctx.font = `500 11px ${family}`
      let widest = 0
      for (const item of this.visibleBookings) {
        const display = this.getStatusDisplay(item)
        let w = ctx.measureText(display.label || '').width + PILL_H_PADDING + PILL_BORDER
        if (this.isStatusEditable(item)) w += CARET
        if (w > widest) widest = w
      }
      return Math.min(MAX, Math.max(MIN, Math.ceil(widest + CELL_PADDING)))
    },
    // BaseDateNavigator is string-based (yyyy-MM-dd); `current` is kept as a
    // Date internally, so convert at the toolbar boundary only.
    currentIso() {
      if (!this.current) return ''
      if (this.current instanceof Date) {
        return this.current.getFullYear() + '-' +
          ('0' + (this.current.getMonth() + 1)).slice(-2) + '-' +
          ('0' + this.current.getDate()).slice(-2)
      }
      return moment(this.current, 'YYYY-MM-DD').format('YYYY-MM-DD')
    },
    editableStatusOptions() {
      // Built-in unbooked statuses + custom statuses tied to those (parent_status_key)
      const builtIns = [
        { status: 'available', label: 'Available' },
        { status: 'blocked', label: 'Blocked' },
        { status: 'call_to_book', label: 'Call to book' },
        { status: 'requestable', label: 'Requestable' }
      ].map(b => ({ ...b, color: this.getStatusColor(b.status).color }))

      const customs = (this.customStatuses || [])
        .filter(c => !c.archived && (!c.parent_status_key || ['available', 'blocked', 'call_to_book'].includes(c.parent_status_key)))
        .map(c => ({ status: c.status_key || c.id, label: c.display_name, color: c.color }))

      return [...builtIns, ...customs]
    },
    // Schema for the shared <filter-bar>: status keys and game ids as multiselects.
    filterSchema() {
      return [
        { key: 'status', type: 'multiselect', label: 'Status', primary: true,
          options: this.filter_statuses.map(s => ({ label: s.label, value: s.status })) },
        { key: 'game', type: 'multiselect', label: 'Event', primary: true,
          options: this.games.map(g => ({ label: g.name, value: g.id })) },
        { key: 'tags', type: 'multiselect', label: 'Tags',
          options: this.groupTags.map(t => ({ label: t.name, value: t.id })) },
        { key: 'has_waivers', type: 'toggle', label: 'Has waivers', primary: true }
      ]
    },
    // Tag filtering is client-side: the calendar endpoint returns each row's
    // tags array; status/game stay server-side query params.
    // Badge on the phone filter button: every selected status, event and tag
    activeFilterCount() {
      const count = v => (Array.isArray(v) ? v.length : 0)
      return count(this.filterValues.status) + count(this.filterValues.game) + count(this.filterValues.tags)
        + (this.filterValues.has_waivers === true ? 1 : 0)
    },
    rangeWindow() {
      return computeRangeWindow(this.range, this.currentIso, this.customRange, this.first_day_of_week)
    },
    // OTC-1586: the operating window of the day on screen
    dayWindow() {
      return operatingWindow(this.currentIso || moment().format('YYYY-MM-DD'), this.day_boundary)
    },
    rangeLabel() {
      return formatWindowLabel(this.rangeWindow, { date_format: this.date_format })
    },
    // Day: the rows as before. Ranges: a header row per day that has bookings, then that day's rows;
    // a slot spanning several days is listed under each of them.
    tableEntries() {
      if (this.range === 'day') return this.visibleBookings
      const entries = []
      const byStart = (a, b) => String(a.start_time || '').localeCompare(String(b.start_time || ''))
      const group = { date_format: this.date_format, show_day_of_week: this.show_day_of_week_group }
      for (const date of daysInWindow(this.rangeWindow)) {
        const rows = this.visibleBookings
          .filter(b => spannedDaysInWindow(b.booking_date, b.end_date, this.rangeWindow, b.end_time).includes(date))
          .sort(byStart)
        if (rows.length === 0) continue
        entries.push({ __dayHeader: true, date, label: formatRangeDay(date, group), count: rows.length })
        rows.forEach(row => entries.push({ ...row, __groupDate: date }))
      }
      return entries
    },
    visibleBookings() {
      const rows = this.bookings
      const selected = this.filterValues.tags
      if (!Array.isArray(selected) || selected.length === 0) return rows
      return rows.filter(item =>
        Array.isArray(item.tags) && item.tags.some(t => selected.includes(t.id))
      )
    },
    filter_statuses() {
      const base = [
        {id: 1, label: 'Available', status: 'available'},
        {id: 2, label: 'Call to book', status: 'call_to_book'},
        {id: 3, label: 'Blocked', status: 'blocked'},
        {id: 4, label: 'Booked', status: 'booked'},
        {id: 6, label: 'Completed', status: 'completed'},
        {id: 7, label: 'Passed', status: 'expired'},
        {id: 8, label: 'Requested', status: 'requested'},
        {id: 9, label: 'Requestable', status: 'requestable'}
      ]
      const custom = this.customStatuses.map((s, i) => ({
        id: 100 + i,
        label: s.display_name,
        status: s.status_key
      }))
      return [...base, ...custom]
    },
  },
  watch: {
    'modals.workflowStates'(newVal) {
      if (!newVal) {
        this.loadWorkflowStates();
      }
    },
    // Watch for payment option changes to reinitialize Square form when switching back to Card
    selectedPaymentOption: {
      handler(newVal, oldVal) {
        if (newVal?.name === 'Card' && oldVal?.name !== 'Card' && this.payment_settings.payment_gateway_id === 2) {
          // Switching back to Card with Square - reinitialize the form
          this.$nextTick(async () => {
            // Destroy existing card if present
            if (this.card) {
              try {
                await this.card.destroy()
              } catch (e) {
                console.log('Error destroying Square card:', e)
              }
              this.card = null
            }
            // Reinitialize based on context
            if (this.add_payment) {
              this.initializeSquare()
            } else if (this.activeTabIndex === 3) {
              this.initializeSquare('add_payment')
            }
          })
        }
      },
      deep: true
    },
    // Watch for date changes to update subscription (without reconnecting)
    current(newDate, oldDate) {
      if (newDate !== oldDate && newDate) {
        // Update URL to persist date across group switches
        const formattedDate = moment(newDate).format('YYYY-MM-DD')
        if (this.$route.query.date !== formattedDate) {
          this.$router.replace({ query: { ...this.$route.query, date: formattedDate } }).catch(() => {})
        }
        // Update WebSocket subscription (ranges are subscribed to every date already)
        if (this.ws && this.range === 'day' && this.day_boundary === 0) {
          if (this.ws.readyState === WebSocket.OPEN) {
            this.updateWebSocketSubscription(newDate);
          } else {
            // If connection is not open, reconnect
            this.closeWebSocket();
            this.initBookingWebSocket();
          }
        }
      }
    }
  },
  methods: {

    handleGearCommand(cmd) {
      switch (cmd) {
        case 'bulk-actions': this.bulkUpdatePrompt(); break
        case 'booking-slot-settings': this.openBookingSlotSettings(); break
        case 'workflow-states': this.modals.workflowStates = true; break
        case 'show-schedule-name': this.toggleScheduleName(1); break
        case 'hide-schedule-name': this.toggleScheduleName(0); break
        case 'show-staff': this.toggleStaffVisibility(1); break
        case 'hide-staff': this.toggleStaffVisibility(0); break
        case 'calendar-settings': this.showCalendarSettingsDrawer = true; break
      }
    },

    onCalendarSettingsSaved() {
      this.showCalendarSettingsDrawer = false
      // Re-pull the venue policy so the notice indicators reflect the save.
      this.getSettings()
    },

    // ============================================================
    // Notification Helper with Deduplication
    // ============================================================
    showWebSocketNotification(action) {
      // Disabled — success notifications come from mutation callbacks instead
    },

    // ============================================================
    // WebSocket Methods for Real-Time Updates
    // ============================================================
    async initBookingWebSocket() {
      try {
        // Get JWT token for authentication
        const token = await this.getAuthToken();

        // Build WebSocket URL with query parameters
        const wsUrl = 'wss://3bqa7hkquf.execute-api.us-east-1.amazonaws.com/production';
        const currentDate = this.current ? moment(this.current).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
        const params = new URLSearchParams({
          token: token,
          companyGroupId: this.companyGroupId
        });
        // Multi-day modes subscribe to every date in the group (the server stores booking_date NULL and
        // broadcasts every change to it); messages are then filtered by the visible window (OTC-166)
        // A Day with a boundary spans two dates, so it subscribes to every date like the ranges do (OTC-1586)
        if (this.range === 'day' && this.day_boundary === 0) params.set('date', currentDate); // Current selected date in YYYY-MM-DD format

        this.ws = new WebSocket(`${wsUrl}?${params.toString()}`);

        this.ws.onopen = () => {
          const wasReconnecting = this.wsReconnectAttempts > 0;
          this.wsReconnectAttempts = 0;
          this.wsReconnectDelay = 1000;

          // Start heartbeat to prevent 10-minute idle timeout
          this.startHeartbeat();

          // If this was a reconnection, sync data to catch any missed events
          if (wasReconnecting) {
            this.getBookings();
          }
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            // Handle pong responses
            if (message.type === 'pong') {
              return;
            }

            if (message.type === 'booking-changed') {
              // Normalize dates for comparison (handle both YYYY-MM-DD and ISO timestamp)
              const messageDate = message.date ? message.date.split('T')[0] : null;
              const currentDate = moment(this.current).format('YYYY-MM-DD');
              const inView = this.range === 'day'
                ? (messageDate === currentDate || (this.day_boundary > 0 && messageDate === this.dayWindow.endDate))
                : (messageDate >= this.rangeWindow.startDate && messageDate <= this.rangeWindow.endDate);
              if (inView &&
                  parseInt(message.companyGroupId) === parseInt(this.companyGroupId)) {

                // Debounce data refresh to avoid redundant API calls during bursts
                clearTimeout(this.wsRefreshTimer);
                this.wsRefreshTimer = setTimeout(() => { this.getBookings() }, 300);

                // Debounced notification — only one shown per burst
                this.showWebSocketNotification(message.action);
              }
            }
          } catch (error) {
            console.error('❌ Error processing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          // Stop heartbeat
          this.stopHeartbeat();

          // Only reconnect if not intentionally closed (code 1000 = normal closure)
          if (event.code !== 1000) {
            this.handleWebSocketReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
        };

      } catch (error) {
        console.error('❌ Failed to initialize WebSocket:', error);
        this.handleWebSocketReconnect();
      }
    },

    handleWebSocketReconnect() {
      if (this.wsReconnectAttempts >= this.wsMaxReconnectAttempts) {
        console.error('❌ Max WebSocket reconnection attempts reached');
        return;
      }

      this.wsReconnectAttempts++;

      // Exponential backoff
      const delay = Math.min(
        this.wsReconnectDelay * Math.pow(2, this.wsReconnectAttempts - 1),
        30000 // Max 30 seconds
      );

      setTimeout(() => {
        this.initBookingWebSocket();
      }, delay);
    },

    closeWebSocket() {
      if (this.ws) {
        this.stopHeartbeat();
        this.ws.close(1000, 'Component closing');
        this.ws = null;
      }
    },

    startHeartbeat() {
      // Send ping every 4 minutes (well before 10-minute timeout)
      this.wsHeartbeatInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          try {
            this.ws.send(JSON.stringify({
              action: 'ping'
            }));
          } catch (error) {
            console.error('❌ Failed to send heartbeat:', error);
          }
        }
      }, 4 * 60 * 1000); // 4 minutes
    },

    stopHeartbeat() {
      if (this.wsHeartbeatInterval) {
        clearInterval(this.wsHeartbeatInterval);
        this.wsHeartbeatInterval = null;
      }
    },

    handleVisibilityChange() {
      if (!document.hidden) {
        // User returned to the page - check if WebSocket is still alive
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          this.closeWebSocket();
          this.initBookingWebSocket();
        } else {
          // Connection appears open, but verify with a ping
          try {
            this.ws.send(JSON.stringify({
              action: 'ping'
            }));
          } catch (error) {
            console.error('❌ Failed to ping after visibility change, reconnecting...', error);
            this.closeWebSocket();
            this.initBookingWebSocket();
          }
        }
      }
    },

    updateWebSocketSubscription(newDate) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        console.warn('Cannot update subscription - WebSocket not connected');
        return;
      }

      try {
        // Format date to YYYY-MM-DD
        const formattedDate = moment(newDate).format('YYYY-MM-DD');

        // Send subscription update message to server
        const message = JSON.stringify({
          action: 'updateSubscription',
          companyGroupId: this.companyGroupId,
          date: formattedDate
        });

        this.ws.send(message);

      } catch (error) {
        console.error('❌ Failed to update subscription:', error);
        // If update fails, fall back to reconnection
        this.closeWebSocket();
        this.initBookingWebSocket();
      }
    },

    // ============================================================
    // End WebSocket Methods
    // ============================================================


    // Safe parsing helpers for API data that may already be parsed
    safeParseWaivers(booking) {
      if (!booking || !booking.waivers) return []
      return typeof booking.waivers === 'string' ? JSON.parse(booking.waivers) : booking.waivers
    },
    safeParseNotes(booking) {
      if (!booking || !booking.notes) return []
      return typeof booking.notes === 'string' ? JSON.parse(booking.notes) : booking.notes
    },
    // ResourceDrawer event handlers (activity log + delete preflight handled internally)
    validateResourceForDrawer(slot) {
      return this.validateResources(slot.start_date, slot.end_date, slot.start_time, slot.end_time);
    },
    onResourceDrawerClose() {
      this.modals.resourceDrawer = false;
      this.selectedResourceSlot = null;
      this.resourceDrawerInitialStartTime = null;
      this.resourceDrawerInitialEndTime = null;
      this.resourceDrawerInitialView = 'detail';
    },
    onResourceChanged() {
      this.getBookings();
    },
    openResourceDrawerForCreate(startTime = null, endTime = null) {
      this.selectedResourceSlot = null;
      this.resourceDrawerInitialStartTime = startTime;
      this.resourceDrawerInitialEndTime = endTime;
      this.modals.resourceDrawer = true;
    },
    openResourceDrawerForEdit(slot) {
      this.selectedResourceSlot = slot;
      this.resourceDrawerInitialStartTime = null;
      this.resourceDrawerInitialEndTime = null;
      this.resourceDrawerInitialView = 'detail';
      this.modals.resourceDrawer = true;
    },

    // Row-action "View activity log" — opens drawer directly into the activity-log subview.
    viewResourceActivityLog(slot, event) {
      if (event) event.preventDefault();
      this.selectedResourceSlot = slot;
      this.resourceDrawerInitialStartTime = null;
      this.resourceDrawerInitialEndTime = null;
      this.resourceDrawerInitialView = 'activity-log';
      this.modals.resourceDrawer = true;
    },
    formatPhone(country, phone) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    getPaid(slot){
      if(slot.hasOwnProperty('public_slots') && slot.public_slots.length > 0){
        let paid = 0
        for(let n in slot.public_slots){
          paid += slot.public_slots[n].paid
        }
        return paid
      } else {
        return slot.paid
      }
    },
    getDue(slot){
      if(slot.hasOwnProperty('public_slots') && slot.public_slots.length > 0){
        let due = 0
        for(let n in slot.public_slots){
          due += slot.public_slots[n].due
        }
        return due
      } else {
        return slot.due
      }
    },
    getAssociatedSlots(slotId){
      let associatedBookings = []
      for(let n in this.bookings){
        if(slotId === this.bookings[n].master_slot_id){
          associatedBookings.push(this.bookings[n])
        }
      }
      return associatedBookings
    },
    getWaiverCount(waivers){
      return waivers.filter(item => item.not_participating === 0).length
    },
    // Mirrors the autoblocking module's capacity rules (OTC-3144); see the helper.
    getAvailableStaffCount(item) {
      return this.resourceAvailabilityById.get(item.id)
        || getSlotResourceAvailability(item, this.resource_slots, this.categoryRequirementsByGame.get(item.game_id), this.resourceSettings)
    },
    // Category events get one line per required category ("Game master 2/3
    // available"), others one total, matching how the autoblocking module counts each.
    resourceAvailabilityLines(item) {
      const counts = this.getAvailableStaffCount(item)
      if (counts.categories) {
        // Every requirement resolved to 0 for this slot: the engine applies no resource constraint.
        if (counts.categories.length === 0) return ['No resources required']
        return counts.categories.map(c => `${c.name} ${c.available}/${c.total} available`)
      }
      return [`${counts.available}/${counts.total} resources available`]
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    openBookingSlotSettings() {
      this.modals.bookingSlotSettings = true
    },
    onBookingSlotSettingsSaved({ customStatuses, systemStatusOverrides }) {
      this.customStatuses = customStatuses
      this.customStatusMap = new Map(customStatuses.map(s => [s.status_key, s]))
      // Update system status overrides
      if (systemStatusOverrides) {
        this.systemStatusOverrideMap = new Map(systemStatusOverrides.map(s => [s.status_key, s]))
      }
      // Reload bookings to apply new colors
      this.getBookings()
    },
    async loadGroupTags() {
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/tags`
        const response = await axios.get(url, config)
        this.groupTags = response.data || []
      } catch (error) {
        console.log('Tags not available:', error.message)
        this.groupTags = []
      }
    },
    async loadCustomStatuses() {
      try {
        const data = await fetchSlotSettings()
        const allStatuses = data.custom_statuses || []
        // Map includes all statuses (active + inactive) so cards can display soft-deleted status names
        this.customStatusMap = new Map(allStatuses.map(s => [s.status_key, s]))
        // Only active statuses for dropdowns
        this.customStatuses = allStatuses.filter(s => s.is_active)
        // Load system status overrides
        const systemOverrides = data.system_status_overrides || []
        this.systemStatusOverrideMap = new Map(systemOverrides.map(s => [s.status_key, s]))
      } catch (error) {
        console.log('Custom statuses not available:', error.message)
        this.customStatuses = []
        this.customStatusMap = new Map()
        this.systemStatusOverrideMap = new Map()
      }
    },
    /**
     * Get status color with fallback chain:
     * 1. System status override (user-customized color for built-in status)
     * 2. Custom status (user-defined status)
     * 3. Hardcoded default (for built-in statuses)
     * 4. Gray fallback (unknown status)
     */
    getStatusColor(statusKey) {
      // 1. Check for system status override from DB
      if (this.systemStatusOverrideMap.has(statusKey)) {
        const override = this.systemStatusOverrideMap.get(statusKey)
        return { color: override.color, text_color: override.text_color }
      }
      // 2. Check for custom status
      if (this.customStatusMap.has(statusKey)) {
        const custom = this.customStatusMap.get(statusKey)
        return { color: custom.color, text_color: custom.text_color }
      }
      // 3. Fall back to hardcoded default for built-in statuses
      if (this.DEFAULT_STATUS_COLORS[statusKey]) {
        return this.DEFAULT_STATUS_COLORS[statusKey]
      }
      // 4. Unknown status fallback
      return { color: '#6B7280', text_color: '#FFFFFF' }
    },
    getStatusDisplay(item) {
      const status = item.status
      // Pending = booked slot with transaction status === 2 (unpaid/abandoned)
      if (status === 'booked' && item.transaction_status === 2) {
        return { label: 'Pending', color: '#6B7280', text_color: '#FFFFFF' }
      }
      // Custom status
      if (this.customStatusMap.has(status)) {
        const cs = this.customStatusMap.get(status)
        return { label: cs.display_name, color: cs.color, text_color: cs.text_color }
      }
      // Built-in status (system override or default)
      const labels = {
        booked: 'Booked',
        expired: 'Passed',
        available: 'Available',
        completed: 'Completed',
        blocked: 'Blocked',
        call_to_book: 'Call to book',
        cancelled: 'Cancelled',
        requested: 'Requested',
        requestable: 'Requestable'
      }
      const colorInfo = this.getStatusColor(status)
      return { label: labels[status] || status, color: colorInfo.color, text_color: colorInfo.text_color }
    },
    // Minimum booking notice indicator (OTC-384). Touches noticeNow so the
    // rows re-evaluate on each tick without refetching.
    getSlotNotice(item) {
      if (!this.show_notice_indicators) return null
      void this.noticeNow
      return classifySlotNotice(item, this.noticeAnchor, this.notice_display_mode)
    },
    startNoticeTick() {
      if (!this.show_notice_indicators || this.noticeTickInterval) return
      this.noticeTickInterval = setInterval(() => {
        this.noticeNow = Date.now()
      }, NOTICE_TICK_MS)
    },
    isStatusEditable(item) {
      // Booked/completed/cancelled require formal cancellation/refund flow
      // Match the lambda guard at companies-edit-booking case 0
      if (!this.hasPermissions('booking_slot_update')) return false
      const lockedStatuses = ['booked', 'completed', 'cancelled', 'requested']
      return !lockedStatuses.includes(item.status)
    },
    isBookedOrCompleted(item) {
      return item.status === 'booked' || item.status === 'completed'
    },
    getCustomerRowKind(item) {
      if (item.end_user_id && this.isBookedOrCompleted(item)) return 'customer'
      if (this.isBookedOrCompleted(item) && item.public_slots && item.public_slots.length > 0) return 'public'
      if (item.status === 'booked' && !item.transaction_id && (!item.public_slots || item.public_slots.length === 0)) return 'incomplete'
      return null
    },
    getImportantNoteText(item) {
      // Mirror Kanban's importantNote pattern: prefer item.important_note,
      // fall back to the first is_important note in the parsed array.
      if (item.important_note) return item.important_note
      const parsed = this.safeParseNotes(item)
      if (!parsed || parsed.length === 0) return null
      const important = parsed.find(n => n.is_important)
      return important ? important.note : null
    },
    otherNotesCount(item) {
      // Total notes minus the one shown in the preview row.
      const parsed = this.safeParseNotes(item)
      if (!parsed) return 0
      const total = parsed.length
      return total > 0 ? total - 1 : 0
    },
    getNotesCount(item) {
      const parsed = this.safeParseNotes(item)
      return parsed ? parsed.length : 0
    },
    getWaiverDisplayName(w) {
      const first = (w && w.first_name) ? String(w.first_name).trim() : ''
      const last = (w && w.last_name) ? String(w.last_name).trim() : ''
      const full = `${first} ${last}`.trim()
      if (full) return full
      if (w && w.email) return w.email.split('@')[0]
      return 'Guest'
    },
    async fetchWaiversForBooking(item) {
      if (!item || !item.id) return
      const existing = this.waiversByBooking[item.id]
      if (existing && (existing.loading || (Array.isArray(existing.data) && existing.data.length > 0))) return
      this.$set(this.waiversByBooking, item.id, { loading: true, data: null, error: null })
      try {
        const token = await this.getAuthToken()
        const companyId = localStorage.getItem('companyId')
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/bookings/${item.id}/waivers`
        const response = await axios.get(url, { headers: { Authorization: token } })
        let data = response.data
        // API Gateway proxy-style wrapper: { statusCode, body: '<json>', headers, isBase64Encoded }
        if (data && typeof data === 'object' && !Array.isArray(data) && typeof data.body === 'string' && data.statusCode !== undefined) {
          try { data = JSON.parse(data.body) } catch (e) { data = [] }
        }
        // Plain JSON-string body
        if (typeof data === 'string') {
          try { data = JSON.parse(data) } catch (e) { data = [] }
        }
        // Known wrapper shapes — endpoint returns { users: [...] }
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          if (Array.isArray(data.users)) data = data.users
          else if (Array.isArray(data.waivers)) data = data.waivers
          else if (Array.isArray(data.data)) data = data.data
          else data = []
        }
        if (!Array.isArray(data)) data = []
        this.$set(this.waiversByBooking, item.id, { loading: false, data, error: null })
      } catch (err) {
        this.$set(this.waiversByBooking, item.id, { loading: false, data: null, error: err })
      }
    },
    async quickSetStatus(booking, newStatus) {
      if (!booking || booking.status === newStatus) return
      const oldStatus = booking.status
      const index = this.bookings.findIndex(b => b.id === booking.id)
      if (index !== -1) {
        this.$set(this.bookings[index], 'status', newStatus)
      }
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/bookings/${booking.id}/v2`
        // booking_date anchor required: companies-edit-booking:371 starts the SET clause
        // unprefixed and would build invalid SQL if status were the only field.
        // status_type_id needed for custom statuses (matches BookingDrawer.onStatusChange).
        const customStatus = (this.customStatuses || []).find(c => c.status_key === newStatus)
        await axios.put(url, {
          booking_date: String(booking.booking_date).substring(0, 10),
          status: newStatus,
          status_type_id: customStatus ? customStatus.id : null
        }, config)
      } catch (error) {
        console.error('Failed to update slot status:', error)
        if (index !== -1) {
          this.$set(this.bookings[index], 'status', oldStatus)
        }
        const message = error.response && error.response.data ? error.response.data : 'Failed to update status'
        this.$notify({ type: 'danger', message, timeout: 4000 })
      }
    },
    async toggleStaffVisibility(status){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/staff/settings'

      let data = {
        add_staff_to_booking_slots: status
      }
      axios.put(url, data, config)
        .then(response => {
          swal('Success!', 'Calendar settings have been updated', 'success')
          this.getSettings()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    // BaseDateNavigator (prev / next / today / picker) emits the new yyyy-MM-dd
    // string; keep `this.current` a Date so the rest of the page is unchanged.
    onRangeChange(value){
      const wasDay = this.range === 'day'
      this.range = value
      // Crossing between Day (one date) and a range (every date) changes the live-update subscription
      if (wasDay !== (value === 'day') && this.ws) {
        this.closeWebSocket()
        this.initBookingWebSocket()
      }
      const state = { range: value, customRange: this.customRange }
      persistRangeState('list', state)
      this.$router.replace({ query: applyRangeToQuery(this.$route.query, state) }).catch(() => {})
      this.getBookings()
    },
    // A day header's date opens that date in Day (OTC-2953)
    async onRangeOpenDay(iso){
      if (!iso) return
      // Date and range go to the URL in one navigation: the route guard is async, so two
      // back-to-back replaces would each start from the same stale query
      const query = { ...applyRangeToQuery(this.$route.query, { range: 'day', customRange: this.customRange }), date: iso }
      await this.$router.replace({ query }).catch(() => {})
      this.current = moment(iso, 'YYYY-MM-DD').toDate()
      this.j++
      // Let the date watcher run while still in a range, so it leaves the live-update
      // subscription to onRangeChange
      await this.$nextTick()
      this.onRangeChange('day')
    },
    onCustomRangeChange(value){
      if (!Array.isArray(value) || value.length !== 2) return
      this.customRange = value
      const state = { range: this.range, customRange: value }
      persistRangeState('list', state)
      this.$router.replace({ query: applyRangeToQuery(this.$route.query, state) }).catch(() => {})
      this.getBookings()
    },
    onNavDate(iso){
      this.current = iso ? moment(iso, 'YYYY-MM-DD').toDate() : new Date()
      this.j++
      this.getBookings()
    },
    hasCustomerAlerts(item){
      if(!item || !item.customer_alerts) return false
      const alerts = typeof item.customer_alerts === 'string'
        ? JSON.parse(item.customer_alerts)
        : item.customer_alerts
      return Array.isArray(alerts) && alerts.length > 0
    },
    hasAddons(item){
      if(!item) return false
      return !!(item.has_addons || item.has_addons_from_children)
    },
    toggleScheduleName(enable){
      if(enable){
        this.show_schedule_name = 1
        localStorage.setItem('showScheduleNameOnCalendar', '1')
      } else {
        this.show_schedule_name = 0
        localStorage.removeItem('showScheduleNameOnCalendar')
      }
    },
    bulkUpdatePrompt(event){
      if (event && typeof event.preventDefault === 'function') event.preventDefault()
      this.modals.bulk_update_slots = true
    },
    onBulkActionsUpdated(){
      this.modals.bulk_update_slots = false
      this.getBookings()
    },
    async deleteResourceSlotPrompt(item, event, source){
      event.preventDefault()
      let validator = await this.validateResources(item.start_date, item.end_date, item.start_time, item.end_time)
      if(validator !== 'Success'){
        swal({
          title: 'Are you sure?',
          text: validator,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'I am sure'
        }).then((response)=> {
          if(response.value == true){
            this.deleteResourceSlot(item)
          }
        })
      } else {
        swal({
          title: 'Are you sure?',
          text: "The resource slot will be permanently deleted.",
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'I am sure'
        }).then((response)=> {
          if(response.value == true){
            this.deleteResourceSlot(item)
          }
        })
      }
    },
    async validateResources(start_dt, end_dt, start_t, end_t) {
      let start = moment(start_dt, "YYYY-MM-DD").format("YYYY-MM-DD");
      let end = moment(end_dt, "YYYY-MM-DD").format("YYYY-MM-DD");
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/'+ companyId + '/groups/' + companyGroupId +
        '/staff/validate?start=' + start + '&end=' + end

      return new Promise((resolve) => {
        axios.get(url, config)
          .then(res => {
            resolve(res.data)
          })
          .catch(err => {
            console.log(err)
            if(err.response) {
              if(err.response.status === 401){
                this.$router.push('/forbidden')
                resolve('Unauthorized')
              } else if(err.response.status === 500) {
                resolve(err.response.data || 'Insufficient resources available')
              } else {
                resolve('An error occurred during validation')
              }
            } else {
              resolve('Network error - please try again')
            }
          })
      })
    },
    countOverlappingEvents(resource_slots, start, end, selfId = null, includeSelf = false) {
      let overlaps = 0;

      for (let evt of resource_slots) {
        const isSelf = selfId !== null && String(evt.id) === String(selfId);
        if (isSelf && !includeSelf) continue;

        const evtStart = moment(evt.start_date).set(moment(evt.start_time, ["hh:mm A", "HH:mm"]).toObject());
        const evtEnd   = moment(evt.end_date).set(moment(evt.end_time, ["hh:mm A", "HH:mm"]).toObject());

        if (!evtStart.isValid() || !evtEnd.isValid()) {
          console.warn("Invalid event:", evt);
          continue;
        }

        const doesOverlap = start.isBefore(evtEnd) && end.isAfter(evtStart);
        if (doesOverlap) {
          overlaps++;
        }
      }

      return overlaps;
    },
    updateResourceSlotPrompt(item, event){
      if (event) event.preventDefault()
      // Compute priorities from current overlap, then hand the raw slot to the drawer.
      const st = moment(item.start_date).set(moment(item.start_time, ["hh:mm A", "HH:mm"]).toObject());
      const ed = moment(item.end_date).set(moment(item.end_time, ["hh:mm A", "HH:mm"]).toObject());
      const overlapCount = this.countOverlappingEvents(this.resource_slots, st, ed, item.id, false);
      this.priorities = Array.from({ length: Math.max(overlapCount + 1, 5) }, (_, i) => i + 1);
      this.openResourceDrawerForEdit(item);
    },
    newResourceSlot(event){
      if (event) event.preventDefault()
      // Compute priorities from current overlap, defaulting to a 10am-8pm window for create.
      const today = new Date();
      const st = moment(today).set({ hour: 10, minute: 0, second: 0 });
      const ed = moment(today).set({ hour: 20, minute: 0, second: 0 });
      const overlapCount = this.countOverlappingEvents(this.resource_slots, st, ed, null, true);
      this.priorities = Array.from({ length: Math.max(overlapCount + 1, 5) }, (_, i) => i + 1);
      this.openResourceDrawerForCreate('10:00:00', '20:00:00');
    },
    async deleteResourceSlot(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/staff/' + item.id

      this.axios.delete(url, config)
        .then(response => {
          swal('Success!', 'Resource slot has been deleted', 'success')
          this.getBookings()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    getCart(){
      if(!JSON.parse(localStorage.getItem('cart'))){
        return 0
      }
      return parseInt(JSON.parse(localStorage.getItem('cart')).length)
    },
    async initializeCardAddPayment(payments){
      const customStyle = {
        '.input-container.is-focus': {
          borderColor: '#e14eca',
        },
        '.input-container.is-error': {
          borderColor: '#ff1600',
        },
        '.message-text.is-error': {
          color: '#ff1600',
        },
        '.message-icon.is-error': {
          color: '#ff1600',
        },
        'input.is-error': {
          color: '#ff1600',
        },
        '.message-icon': {
          color: '#adb5bd',
        },
        '@media screen and (max-width: 600px)': {
          'input': {
            'fontSize': '12px',
          }
        },
        '.input-container': {
          borderRadius: '6px',
        },
        '.message-text': {
          color: '#adb5bd',
          // 'font-family': this.bss.font_family_card
        },
        input: {
          backgroundColor: localStorage.getItem('mode') === '0' ? '#ffffff' : '#22233a',
          color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff',
        },
        'input::placeholder': {
          color: '#adb5bd',
        },
      }
      const card = await payments.card({
        style: customStyle,
      });
      await card.attach('#card-container-2');
      return card;
    },
    async initializeCard(payments) {
      const customStyle = {
        '.input-container.is-focus': {
          borderColor: '#e14eca',
        },
        '.input-container.is-error': {
          borderColor: '#ff1600',
        },
        '.message-text.is-error': {
          color: '#ff1600',
        },
        '.message-icon.is-error': {
          color: '#ff1600',
        },
        'input.is-error': {
          color: '#ff1600',
        },
        '.message-icon': {
          color: '#adb5bd',
        },
        '@media screen and (max-width: 600px)': {
          'input': {
            'fontSize': '12px',
          }
        },
        '.input-container': {
          borderRadius: '6px',
        },
        '.message-text': {
          color: '#adb5bd',
          // 'font-family': this.bss.font_family_card
        },
        input: {
          backgroundColor: localStorage.getItem('mode') === '0' ? '#ffffff' : '#22233a',
          color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff',
        },
        'input::placeholder': {
          color: '#adb5bd',
        },
      }
      const card = await payments.card({
        style: customStyle,
      });
      await card.attach('#card-container');
      return card;
    },
    goToCart(){
      this.$router.push('/bookings/transactions/new')
    },
    async goToCalendar(){
      //update profile settings
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'users/site-settings'

      let data = {
        booking_page_mode: 1
      }

      axios.put(url, data, config)
        .then(response => {
          let profile = JSON.parse(localStorage.getItem('profile'))
          profile.booking_page_mode = 1
          localStorage.setItem('profile', JSON.stringify(profile))
          const dateParam = this.current ? moment(this.current).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
          this.$router.push({ path: '/bookings/calendar', query: { date: dateParam } })
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    async goToResourceCalendar(){
      await this.updateBookingPageMode(2)
      const dateParam = this.current ? moment(this.current).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
      this.$router.push({ path: '/bookings/resource-calendar', query: { date: dateParam } })
    },
    async goToKanban(){
      await this.updateBookingPageMode(3)
      const dateParam = this.current ? moment(this.current).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
      this.$router.push({ path: '/bookings/kanban', query: { date: dateParam } })
    },
    async updateBookingPageMode(mode) {
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = API_LOCATION + 'users/site-settings'
        await axios.put(url, { booking_page_mode: mode }, config)
        let profile = JSON.parse(localStorage.getItem('profile'))
        if (profile) {
          profile.booking_page_mode = mode
          localStorage.setItem('profile', JSON.stringify(profile))
        }
      } catch (err) {
        console.log(err)
      }
    },
    // FilterBar change: apply new filter values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values){
      this.filterValues = {
        status: Array.isArray(values.status) ? values.status : [],
        game: Array.isArray(values.game) ? values.game : []
      }
      this.didInitialFetch = true
      this.getBookings()
    },
    getFormattedDate(d){
      if(d instanceof Date){
        d = d.getFullYear() + '-' + (d.getMonth()+1) + '-' + d.getDate()
      }
      return formattedDate(this.date_format, d)
    },
    newBookingSlot(event){
      if(event){
        event.preventDefault()
      }
      // Route through CreateSlotDrawer (matches Calendar.vue). The drawer fetches its own data and submits via /booking_slots
      // — we just hand it the click context. No date/time pre-fill here since
      // the dropdown action isn't tied to a calendar drag; the drawer applies
      // its 1-hour default off the current local time.
      this.createSlotGame = (this.games && this.games.length > 0) ? this.games[0] : null;
      this.createSlotDate = this.current || moment().format('YYYY-MM-DD');
      this.createSlotStartTime = null;
      this.createSlotEndTime = null;
      this.modals.createSlotDrawer = true;
    },
    onCreateSlotDrawerClose() {
      this.modals.createSlotDrawer = false;
      this.createSlotGame = null;
      this.createSlotDate = null;
      this.createSlotStartTime = null;
      this.createSlotEndTime = null;
    },
    onCreateSlotDrawerCreated() {
      this.onCreateSlotDrawerClose();
      this.getBookings();
    },
    async getPaymentMethods(){
      fetchPaymentMethods()
        .then((data) => {
          this.paymentOptions = data.payment_methods
          this.selectedPaymentOption = this.paymentOptions.length > 0 ?
            this.paymentOptions[0] : {name: '', content: '', id: '', is_default: ''}
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    /**
     * Mobile uses an explicit Edit button to open the drawer; desktop keeps the
     * full-row click as a convenience. Bail if viewport is mobile-sized.
     */
    handleBookingRowClick(item){
      if (window.innerWidth <= 991) return
      this.openBookingDrawerForRow(item)
    },
    /**
     * Pull the human-readable order_number from the booking. The bookings list
     * lambda surfaces it as a top-level string field per transaction. Falls
     * back to transaction_id only when the field is missing (older deployments).
     */
    getOrderNumber(item){
      if (!item) return null
      if (item.order_number) return item.order_number
      return item.transaction_id
    },
    setToday(){
      let current = new Date()
      this.current = current
    },
    getAmount(integer) {
      // Handle null/undefined but allow 0
      if (integer == null) {
        integer = 0;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async loadWorkflowStates() {
      try {
        this.workflowStates = (await fetchWorkflowStates()) || []
      } catch (error) {
        console.error('Failed to load workflow states:', error)
      }
    },
    getWorkflowState(booking) {
      if (!booking || !booking.workflow_state_id) return null
      return this.workflowStates.find(s => s.id === booking.workflow_state_id) || null
    },
    getWorkflowBadgeStyle(booking) {
      const state = this.getWorkflowState(booking)
      if (!state) return {}
      const hex = state.color.replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16) / 255
      const g = parseInt(hex.substring(2, 4), 16) / 255
      const b = parseInt(hex.substring(4, 6), 16) / 255
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b
      const bg = luminance > 0.55 ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.95)'
      return {
        backgroundColor: bg,
        color: state.color,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.35)'
      }
    },
    async quickSetWorkflowState(booking, stateId) {
      if (stateId === 'clear') stateId = null
      if (!booking || booking.workflow_state_id === stateId) return
      const oldStateId = booking.workflow_state_id
      const index = this.bookings.findIndex(b => b.id === booking.id)
      if (index !== -1) {
        this.$set(this.bookings[index], 'workflow_state_id', stateId)
      }
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        await axios.put(
          `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/bookings/${booking.id}/workflow-state`,
          { workflow_state_id: stateId },
          config
        )
      } catch (error) {
        console.error('Failed to update workflow state:', error)
        if (index !== -1) {
          this.$set(this.bookings[index], 'workflow_state_id', oldStateId)
        }
        this.$notify({ type: 'danger', message: 'Failed to update workflow state', timeout: 3000 })
      }
    },
    async getGames () {
      this.loading = true
      try {
        this.games = await fetchGames()
        this.loading = false
      } catch (err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },
    async getBookings(){
      let selected_booking_id = ''
      let deep_link_date = null
      if(this.$route.hasOwnProperty('query') && this.$route.query.hasOwnProperty('selected_booking')){
        selected_booking_id = this.$route.query.selected_booking
        deep_link_date = await resolveDeepLinkDate(selected_booking_id, this.deepLinkDate)
        const query = { ...this.$route.query };
        if ('selected_booking' in query) {
          delete query.selected_booking;
          this.$router.replace({ query }).catch(err => {
            if (err.name !== 'NavigationDuplicated') throw err;
          });
        }
      }

      // FilterBar values: status = array of status keys, game = array of game ids.
      // status=checked from an old saved view or URL reads as the Has waivers toggle.
      const { status, hasWaivers } = normalizeStatusFilter(this.filterValues)
      const game_id = Array.isArray(this.filterValues.game) ? this.filterValues.game.join(',') : ''

      if(deep_link_date){
        this.current = new Date(deep_link_date + 'T00:00:00')
      }
      let current = this.current
      if(this.current instanceof Date){
        current = this.current.getFullYear() + '-' +  ('0' + (this.current.getMonth()+1)).slice(-2) + '-' + ('0' +this.current.getDate()).slice(-2)
      } else {
        current = moment(this.current, 'YYYY-MM-DD').format('YYYY-MM-DD')
      }

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const statusParam = '&status=' + status.toLowerCase().replace(/\s/g, "_") + '&game=' + game_id + (hasWaivers ? '&has_waivers=1' : '')
      let url = this.range === 'day'
        ? API_LOCATION + 'companies/' + companyId +  '/groups/' + companyGroupId +'/bookings/calendar?type=3&day=' + current
          + (this.dayWindow.endDate !== this.dayWindow.startDate ? '&through=' + this.dayWindow.endDate : '') + statusParam
        : API_LOCATION + 'companies/' + companyId +  '/groups/' + companyGroupId +'/bookings/calendar?type=3&start_date='
          + this.rangeWindow.startDate + '&end_date=' + this.rangeWindow.endDate + statusParam

      // Only the latest request may write results: quick date or range changes must not let a
      // slower earlier response replace the one the user is now looking at (OTC-166)
      const seq = (this._bookingsSeq = (this._bookingsSeq || 0) + 1)
      this.axios.get(url, config)
        .then(response => {
          if (seq !== this._bookingsSeq) return
          this.pricing_types = response.data.pricing_types
          this.bookings = response.data.bookings
          this.noticeAnchor = captureNoticeAnchor(response.data.server_now)
          this.resource_slots = response.data.resource_slots
          const requirementsByGame = new Map()
          for (const r of response.data.category_requirements || []) {
            if (!requirementsByGame.has(r.game_id)) requirementsByGame.set(r.game_id, [])
            requirementsByGame.get(r.game_id).push(r)
          }
          this.categoryRequirementsByGame = requirementsByGame
          const settings = response.data.resource_settings || {}
          this.resourceSettings = {
            crossGameBuffers: settings.cross_game_buffers !== false,
            requireFullBufferCoverage: settings.require_full_buffer_coverage === true
          }
          this.payment_settings = response.data.payment_settings
          // Cast boolean settings for el-switch components
          if (this.payment_settings) {
            if (this.payment_settings.store_credit_card !== undefined) {
              this.payment_settings.store_credit_card = Boolean(this.payment_settings.store_credit_card)
            }
            if (this.payment_settings.square_store_credit_card !== undefined) {
              this.payment_settings.square_store_credit_card = Boolean(this.payment_settings.square_store_credit_card)
            }
          }
          this.enable_utm_source_tracking = response.data.enable_utm_source_tracking

          for(let n in this.resource_slots){
            this.resource_slots[n].linked_games = typeof this.resource_slots[n].linked_games === 'string'
              ? JSON.parse(this.resource_slots[n].linked_games)
              : this.resource_slots[n].linked_games
            // Parsed once here so the per-row availability count never re-parses it.
            this.resource_slots[n].assignment = typeof this.resource_slots[n].assignment === 'string'
              ? JSON.parse(this.resource_slots[n].assignment)
              : this.resource_slots[n].assignment
          }

          if(this.bookings.length <= 0){
            this.empty = true
          } else {
            this.empty = false
          }
          this.taxes = response.data.taxes
          this.fees = response.data.fees

          for(let n in this.bookings){
            let child_slots = _.filter(response.data.bookings, {master_slot_id: this.bookings[n].id})

            if(child_slots.length > 0){
              let child_waivers = []
              let any_child_has_addons = false

              for(let m in child_slots){
                if(child_slots[m].waivers){
                  const waivers = typeof child_slots[m].waivers === 'string'
                    ? JSON.parse(child_slots[m].waivers)
                    : child_slots[m].waivers
                  child_waivers = child_waivers.concat(waivers)
                }

                if(child_slots[m].has_addons){
                  any_child_has_addons = true
                }
              }

              if(this.bookings[n].waivers === null){
                this.bookings[n].waivers = JSON.stringify(child_waivers)
              } else {
                const existingWaivers = typeof this.bookings[n].waivers === 'string'
                  ? JSON.parse(this.bookings[n].waivers)
                  : this.bookings[n].waivers
                this.bookings[n].waivers = JSON.stringify(existingWaivers.concat(child_waivers))
              }

              // Set master slot's has_addons_from_children flag if any child has addons
              if(any_child_has_addons){
                this.bookings[n].has_addons_from_children = true
              }

              // Store child slots for later access when viewing addons
              this.bookings[n].child_slots = child_slots
            }
          }

          //Check if item is in cart and mark it accordingly
          this.bookings = this.bookings
            .map((b) => {
              b.public_slots = this.getAssociatedSlots(b.id)
              b.staff = typeof b.staff === 'string' ? JSON.parse(b.staff) : b.staff
              b.custom_pricing = typeof b.custom_pricing === 'string' ? JSON.parse(b.custom_pricing) : b.custom_pricing
              b.pricing_type = this.pricing_types[0]

              b.has_addons = !!b.has_addons

              if (localStorage.getItem('cart')) {
                let cart = JSON.parse(localStorage.getItem('cart'))
                for (let m in cart) {
                  if (cart[m].id === b.id) {
                    b.in_cart = true
                  }
                }
              }

              let startDate = moment(b.booking_date, 'YYYY-MM-DD')
              let startTime = moment(b.start_time, 'HH:mm:ss')
              startDate.set({
                hour: startTime.hour(),
                minute: startTime.minute(),
                second: startTime.second()
              })
              let endDate = moment(b.end_date, 'YYYY-MM-DD')
              let endTime = moment(b.end_time, 'HH:mm:ss')
              endDate.set({
                hour: endTime.hour(),
                minute: endTime.minute(),
                second: endTime.second()
              })

              if (!startDate.isBefore(endDate)) {
                b.end_date = b.start_date
              }

              b._normalizedStartDate = startDate.clone().startOf('day') // cache for filtering
              return b
            })
            .filter(b => {
              if (b.master_slot_id !== null) return false
              // Ranges keep every master slot in the window; Day keeps only slots starting on the date
              if (this.range !== 'day') return true
              let selectedDate = moment(this.current, 'YYYY-MM-DD').startOf('day')
              if (this.day_boundary === 0) return b._normalizedStartDate.isSame(selectedDate, 'day')
              // With a day boundary, Day keeps the slots that START inside the operating
              // window: the date from the boundary on, plus the next date before it (OTC-1586)
              if (b.is_all_day) return b._normalizedStartDate.isSame(selectedDate, 'day')
              const startMinutes = extendedMinutes(b.booking_date, b.start_time, this.dayWindow.startDate)
              return startMinutes >= this.dayWindow.startMinutes && startMinutes < this.dayWindow.endMinutes
            })

          if(this.payment_settings.payment_gateway_id === 2){
            this.squareAppId = this.payment_settings.api_name
            this.squareLocationId = this.payment_settings.api_location
            this.loadSquare(this.payment_settings.is_live)
          }
          if(this.payment_settings.payment_gateway_id === 3){
            this.loadStripe()
          }
          this.j++
          this.loading = false

          if(selected_booking_id){
            this.openDeepLinkedBooking(selected_booking_id, deep_link_date)
          }
          this.addQueryParameters()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            // this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    // Build the drawer-shaped booking card that BookingDrawer expects from
    // a row-shaped booking object (mirrors Calendar.vue:buildDrawerBooking).
    // The drawer fetches its own data via /bookings/{id}/drawer; this just
    // hands it enough context to render the header and trigger the fetch.
    buildDrawerBooking(rawBooking) {
      if (!rawBooking) return null;
      const bookingDate = rawBooking.booking_date
        ? String(rawBooking.booking_date).substring(0, 10)
        : null;
      const endDate = rawBooking.end_date
        ? String(rawBooking.end_date).substring(0, 10)
        : bookingDate;
      const customerName = `${rawBooking.first_name || ''} ${rawBooking.last_name || ''}`.trim()
        || (rawBooking.status === 'blocked' ? 'Blocked' : 'Available');
      // Times in Bookings.vue are already 24-hour strings — no AM/PM mutation
      // like Calendar.vue does. Pass through verbatim.
      return {
        id: rawBooking.id,
        gameId: rawBooking.game_id,
        gameName: rawBooking.game_name,
        gameImage: rawBooking.game_image || rawBooking.img,
        customerName,
        groupSize: rawBooking.group_size || 0,
        startTimeRaw: rawBooking.start_time,
        endTimeRaw: rawBooking.end_time,
        bookingDate,
        endDate,
        status: rawBooking.status,
        workflowStateId: rawBooking.workflow_state_id || null,
        originalData: rawBooking
      };
    },
    openBookingDrawerForRow(rawBooking, view = 'detail', section = null) {
      const booking = this.buildDrawerBooking(rawBooking);
      if (!booking) return;
      if (!canAppendToSlot(rawBooking)) {
        this.$notify({
          type: 'warning',
          message: 'This slot is already booked. Pick an open slot to add to the transaction.',
          verticalAlign: 'top',
          horizontalAlign: 'right',
          timeout: 4000
        });
        return;
      }
      this.drawerInitialView = view;
      this.drawerInitialSection = section;
      this.selectedBookingCard = booking;
      this.modals.bookingDrawer = true;
    },
    onBookingDrawerClose() {
      this.modals.bookingDrawer = false;
      this.selectedBookingCard = {};
      this.drawerInitialView = 'detail';
      this.drawerInitialSection = null;
    },
    onBookingDrawerChanged() {
      // Refresh the bookings list so any drawer-side change (status, workflow,
      // payment, cancel, etc.) reflects in the list view.
      this.getBookings();
    },
    onAddToCartFromDrawer(cartItem) {
      this.modals.bookingDrawer = false;
      if (!cartItem) return;
      let cart = JSON.parse(localStorage.getItem('cart')) || [];
      const cart_string = localStorage.getItem('cart') || '';
      if (!cart_string.includes(cartItem.id)) {
        cartItem.tickets = [];
        const game = (this.games || []).find(g => g.id === cartItem.game_id);
        if (game) {
          cartItem.group_size = game.max_players_count;
          cartItem.min_players_count = game.min_players_count;
          cartItem.max_players_count = game.max_players_count;
          const gameObj = {
            game_name: game.game_name,
            hide_pricing_rate: cartItem.is_custom_pricing_enabled ? cartItem.hide_pricing_rate : game.hide_pricing_rate,
            game_pricing: game.game_pricing,
            tax: 0,
            is_custom_pricing_enabled: cartItem.is_custom_pricing_enabled,
            pricing_type_id: cartItem.is_custom_pricing_enabled ? cartItem.pricing_type_id : game.pricing_type_id,
            cumulative_category_pricing: cartItem.is_custom_pricing_enabled ? cartItem.cumulative_category_pricing : game.cumulative_category_pricing,
            custom_pricing: cartItem.custom_pricing
          };
          cart.push(_.merge(cartItem, gameObj));
        } else {
          cart.push(cartItem);
        }
        localStorage.setItem('cart', JSON.stringify(cart));
        localStorage.setItem('taxes', JSON.stringify(this.taxes));
        localStorage.setItem('fees', JSON.stringify(this.fees));
      }
      localStorage.setItem('payment_settings', JSON.stringify(this.payment_settings));
      window.location.href = '/bookings/transactions/new';
    },
    // Opens the deep-linked booking. When this page's filters hide it, the row comes
    // from an unfiltered fetch of its date, so the drawer opens and the filters stay.
    async openDeepLinkedBooking(bookingId, bookingDate){
      let item = bookingDate ? _.find(this.bookings, {id: Number(bookingId)}) : null
      if(!item && bookingDate) item = await fetchDeepLinkSlot(bookingId, bookingDate)
      if(item){
        this.editBooking(item)
      } else {
        this.$notify({
          type: 'warning',
          message: bookingDate ? "This booking isn't shown in this view." : "This booking couldn't be found.",
          verticalAlign: 'top',
          horizontalAlign: 'right',
          timeout: 4000
        })
      }
    },
    async editBooking(item, event){
      if(event){
        event.preventDefault()
      }
      if(!item) return;
      const status = item.status;
      if(!this.hasPermissions('bookings_update') && (status === 'booked' || status === 'completed')){
        return;
      }
      if(!this.hasPermissions('booking_slot_update') && !this.hasPermissions('bookings_create')){
        return;
      }
      this.openBookingDrawerForRow(item);
    },
    getTime(d){
      if(!d){
        return
      }
      return time(this.time_format, d)
    },
    async loadStripe(){
      const stripeExists = document.getElementById('stripev3');
      if (!stripeExists) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.id = 'stripev3';
        document.body.appendChild(script);
      }
    },
    async loadSquare(is_live){
      const script = document.createElement('script');
      if(is_live){
        script.src = 'https://web.squarecdn.com/v1/square.js'
      } else {
        script.src = 'https://sandbox.web.squarecdn.com/v1/square.js'
      }
      script.async = true;
      document.body.appendChild(script);
    },
    async initializeSquare(type){
      if (!window.Square) {
        throw new Error('Square.js failed to load properly');
      }
      if(this.card){
        await this.card.destroy()
      }
      this.square = window.Square.payments(this.squareAppId, this.squareLocationId);
      try {
        if(type == 'add_payment'){
          this.card = await this.initializeCardAddPayment(this.square);
        } else {
          this.card = await this.initializeCard(this.square);
        }

      } catch (e) {
        console.error('Initializing Card failed', e);
        return;
      }
    },
    async getSettings(){
      fetchSiteSettings()
        .then(data => {
          this.staff_settings = data.staff_settings
          this.show_staff_settings  = data.show_staff_settings
          const policy = data.calendar_policy_settings || {}
          this.show_notice_indicators = Boolean(policy.show_notice_indicators)
          this.notice_display_mode = policy.notice_display_mode || 'indicator'
          this.can_edit_calendar_policy = Boolean(data.can_edit_calendar_policy)
          this.startNoticeTick()
          if(this.staff_settings){
            this.fields = ["time", "booking", "booked_by", "number_of_players", "paid", "due", "notes", "staff", "actions"]
          } else {
            this.fields = ["time", "booking", "booked_by", "number_of_players", "paid", "due", "notes", "actions"]
          }
          // Cache the preference so the /bookings redirect and per-page mount
          // guards can route directly next time without mounting+fetching here.
          cacheBookingPageMode(data.booking_page_mode)
          // Skip the preferred-view redirect when the user explicitly opened a
          // saved view for this page (captured at mount before FilterBar strips
          // the ?view= param).
          if(!this.cameFromSavedView){
            if(data.booking_page_mode == 1){
              // Forward the existing query so onboarding/categoryId/taskId and any other
              // params survive the user-preference redirect (otherwise the wizard side
              // panel loses context when this page bounces the user to their preferred view).
              // stripViewFilterParams drops this view's status/game so the target view
              // doesn't inherit filters the user never set there.
              this.$router.push({ path: '/bookings/calendar', query: withDeepLinkBooking(stripViewFilterParams(this.$route.query), this.deepLinkBookingId, this.deepLinkDate) })
            } else if(data.booking_page_mode == 2){
              this.$router.push({ path: '/bookings/resource-calendar', query: withDeepLinkBooking(stripViewFilterParams(this.$route.query), this.deepLinkBookingId, this.deepLinkDate) })
            } else if(data.booking_page_mode == 3){
              this.$router.push({ path: '/bookings/kanban', query: withDeepLinkBooking(stripViewFilterParams(this.$route.query), this.deepLinkBookingId, this.deepLinkDate) })
            }
          }
          this.deepLinkBookingId = null
          // this.getBookings()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    addQueryParameters() {
      // This runs deferred (from fetch .then callbacks), so a preferred-view
      // redirect may already have navigated away. A pathless replace targets
      // whatever route is CURRENT, which would stamp this view's status/game
      // onto the other view's URL — the calendar-inherits-list-filters bug.
      if (this.$route.path !== '/bookings/list') return
      const { status: statusValue, hasWaivers } = normalizeStatusFilter(this.filterValues)
      const gameValue = Array.isArray(this.filterValues.game) ? this.filterValues.game.join(',') : ''

      const newQuery = {
        ...this.$route.query,
        date: moment(this.current).format('YYYY-MM-DD'),
        status: statusValue,
        game: gameValue,
      }
      if (hasWaivers) newQuery.has_waivers = '1'
      else delete newQuery.has_waivers
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
      // Per-user persistence now lives server-side via FilterBar's saved views
      // (last-used row), replacing the old localStorage bookingFilters write.
    },
  },
  mounted() {

    // If the user's preferred bookings view isn't this list page, bounce there
    // BEFORE firing any of the data fetches below. Without this guard a
    // calendar/resource/kanban user lands here (the default /bookings target),
    // pays a full list-page data load they never see, and only then gets
    // redirected by getSettings, double-loading every endpoint. The redirect in
    // getSettings stays as a backstop for a stale/empty cache.
    // A ?view= param means the user explicitly opened a saved view for THIS
    // page from the saved-views drawer — honor the requested route instead of
    // bouncing to their stored preferred view. Capture it synchronously NOW:
    // FilterBar strips the param from the URL after its async load resolves, so
    // the later async getSettings redirect can't rely on reading the query.
    this.cameFromSavedView = !!this.$route.query.view
    // Kept for the settings redirect below: getBookings strips selected_booking
    // from the URL, and on a first-ever load it can do so before that redirect runs.
    this.deepLinkBookingId = this.$route.query.selected_booking || null
    // The link's own date, before the page writes its default date into the URL
    this.deepLinkDate = this.deepLinkBookingId ? (this.$route.query.date || null) : null
    const preferred = getPreferredBookingsPath()
    if (!this.cameFromSavedView && preferred && preferred !== this.$route.path) {
      // Strip this view's status/game so the preferred view doesn't inherit them.
      this.$router.replace({ path: preferred, query: stripViewFilterParams(this.$route.query) })
      return
    }

    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    if (this.time_format === 'HH:mm') {
      this.picker_time_format = 'HH:mm'
    }

    this.country = _.find(this.countries, { code: JSON.parse(localStorage.getItem('group')).country })
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    // Restore date from URL query parameter if available
    if (this.$route.query.date) {
      this.current = new Date(this.$route.query.date + 'T00:00:00')
    } else {
      this.setToday()
    }
    // Restore the range (URL first, then localStorage). Deep links to a booking always open on Day.
    {
      const rangeState = readRangeState('list', window.location.search)
      this.range = this.$route.query.selected_booking ? 'day' : rangeState.range
      this.customRange = rangeState.customRange
    }
    // Seed FilterBar's flat values from the URL now, so FilterBar mounts at once and
    // starts its saved-views request alongside the reference fetches below. Saved
    // views precedence is resolved by FilterBar, which drives the first fetch.
    this.filterValues = {
      status: this.$route.query.status ? this.$route.query.status.split(',') : [],
      game: this.$route.query.game ? this.$route.query.game.split(',').map(Number) : [],
      has_waivers: this.$route.query.has_waivers === '1'
    }
    this.current = this.$route.query.date ? moment(this.$route.query.date).toDate() : moment(this.current).toDate()
    this.gamesLoaded = true
    this.getSettings()
    this.loadWorkflowStates()
    // WebSocket replaces polling - no more setInterval
    this.getGames()
    this.getPaymentMethods()
    this.loadCustomStatuses()
    this.loadGroupTags()

    // Initialize WebSocket for real-time booking updates
    this.initBookingWebSocket()

    // Add page visibility listener to reconnect when user returns
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    // Clean up debounce timers
    clearTimeout(this.wsNotifyTimer);
    clearTimeout(this.wsRefreshTimer);
    clearInterval(this.noticeTickInterval);

    // Close WebSocket connection when component is destroyed
    this.closeWebSocket();

    // Remove visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
}
</script>

<style scoped>
.bookings-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.bookings-empty-state ::v-deep .empty-state-img,
.bookings-empty-state img {
  width: 160px;
  height: 160px;
  object-fit: contain;
  display: block;
  margin: 0 auto 0.5rem;
}
.bookings-empty-state .empty-state-text {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.bookings-empty-state__hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 400px;
  margin: 0.25rem auto 1rem;
}
.white-content .bookings-empty-state__hint {
  color: rgba(34, 42, 66, 0.7);
}

/* Booking status pill (click to edit) */
.booking-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  font-size: clamp(9px, 0.72vw, 11px);
  font-weight: 500;
  border: 1px solid rgba(125, 125, 125, 0.3);
  border-radius: 999px;
  user-select: none;
  white-space: nowrap;
  line-height: 1.5;
  max-width: 100%;
  box-sizing: border-box;
}
.booking-status-pill__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The editable pill sits inside el-dropdown's inline-block wrapper; cap that
   wrapper too, otherwise the pill's max-width resolves against an auto-width
   parent and an over-cap label can never ellipsize. */
.bookings-row td > .el-dropdown {
  max-width: 100%;
}
.booking-status-pill--editable {
  cursor: pointer;
  transition: filter 0.15s ease, border-color 0.15s ease;
}
.booking-status-pill--editable:hover {
  filter: brightness(1.12);
  border-color: rgba(125, 125, 125, 0.6);
}
.booking-status-pill__caret {
  font-size: 10px;
  opacity: 0.6;
}
.booking-status-pill__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  vertical-align: middle;
}

/* Booking row avatar — square with rounded corners (matches app pattern).
   Size scales with viewport so it shrinks alongside the row text on
   narrower desktops. */
.booking-avatar {
  width: clamp(40px, 4.2vw, 64px);
  height: clamp(40px, 4.2vw, 64px);
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
}
.booking-avatar--placeholder {
  background: rgba(125, 125, 125, 0.15);
}

/* Bookings table — plain HTML table for bulletproof column alignment.
   Below 1320px the wrap scrolls horizontally (custom-styled scrollbar);
   below 992px the table flips to a stacked card layout. */
.bookings-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #3a3c54 transparent;
}
/* Chrome / Safari / Edge */
.bookings-table-wrap::-webkit-scrollbar {
  height: 8px;
}
.bookings-table-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.bookings-table-wrap::-webkit-scrollbar-thumb {
  background: #3a3c54;
  border-radius: 4px;
  transition: background 0.15s ease;
}
.bookings-table-wrap::-webkit-scrollbar-thumb:hover {
  background: #4a4d6a;
}
.white-content .bookings-table-wrap {
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}
.white-content .bookings-table-wrap::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
}
.white-content .bookings-table-wrap::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.32);
}
.bookings-table {
  width: 100%;
  min-width: 1180px;
  border-collapse: separate;
  border-spacing: 0 8px;
  table-layout: fixed;
}
/* Multi-day ranges (OTC-166): one header row per day above that day's rows */
.bookings-day-header td {
  padding: 14px 8px 2px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-bottom: 1px solid rgba(125, 125, 125, 0.3);
}
/* Phone toolbar range (below 992px): the filter row only shows when the toolbar's filter button
   opens it (OTC-166, OTC-2955) */
@media (max-width: 991.98px) {
  .bookings-filter-row { display: none; }
  .bookings-filter-row.bookings-filter-row--phone-open { display: flex; }
}
.bookings-day-header__count {
  font-weight: 400;
  opacity: 0.6;
}
/* The date label is a button that opens its date in Day (OTC-2953) */
.bookings-day-header__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  cursor: pointer;
}
.bookings-day-header__link:hover,
.bookings-day-header__link:focus-visible {
  color: #e14eca;
}
.bookings-day-header__link:focus-visible {
  outline: 0;
  text-decoration: underline;
}
/* Staff cell (col 8): icons + label can be long ("0/2 resources available").
   Font dynamically scales with viewport so the full label fits on one line
   at any desktop width — no truncation, no ellipsis. */
.bookings-row > td:nth-child(8) .booking-card__stat-line {
  font-size: clamp(8px, 0.72vw, 11px);
  white-space: nowrap;
}
/* Booking-notice line in the status cell (col 2): same dynamic-scale pattern
   as the staff cell so the longest label ("hidden from customers" + lock icon)
   holds one line at any desktop width. The cell's 16px side padding leaves
   only 88px of the 120px column, so the line reclaims room on the right only,
   keeping its left edge flush with the status pill above it. Safe because it
   sits alone on its own row and the spill lands in the gap before the next column. */
.bookings-row .slot-notice {
  font-size: clamp(7.5px, 0.62vw, 8.75px);
  letter-spacing: 0;
  gap: 3px;
  margin-top: 6px;
  margin-left: 0;
  margin-right: -22px;
  max-width: none;
  white-space: nowrap;
}
.bookings-row .slot-notice i {
  font-size: clamp(7.5px, 0.62vw, 8.75px);
}
/* Buttons / chips inside the bookings row also scale so the row keeps
   one-line desktop layout without horizontal scroll. */
.bookings-row .booking-quick-action {
  font-size: clamp(9px, 0.78vw, 12px);
  padding: clamp(5px, 0.5vw, 8px) clamp(8px, 0.95vw, 14px);
}
.bookings-row .booking-alert-chip {
  font-size: clamp(8px, 0.65vw, 10px);
}
.bookings-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.85);
  border-bottom: 1px solid rgba(125, 125, 125, 0.2);
  white-space: nowrap;
}
.bookings-table thead th:first-child {
  padding-left: 22px;
  padding-right: 36px;
}
.bookings-table thead th:last-child {
  padding-right: 22px;
}
.white-content .bookings-table thead th {
  color: rgba(34, 42, 66, 0.85);
  border-bottom-color: rgba(0, 0, 0, 0.1);
}

.bookings-row {
  background: #2c2e48;
  cursor: pointer;
  transition: background 0.15s ease;
  height: 84px;
  color: rgba(255, 255, 255, 0.95);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.bookings-row:hover {
  background: #34365a;
}
.white-content .bookings-row {
  background: #ffffff;
  color: rgba(34, 42, 66, 0.95);
  box-shadow: inset 0 1px 0 rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.04);
}
.white-content .bookings-row:hover {
  background: #f5f6fa;
}
.bookings-row td {
  padding: 12px 16px;
  vertical-align: middle;
  background: inherit;
  height: 84px;
  /* Dynamic font scaling — row text shrinks with viewport so the whole row
     fits on one line at any desktop width, no horizontal scroll. */
  font-size: clamp(11px, 0.85vw, 13px);
}
.bookings-row td:first-child {
  position: relative;
  padding-left: 22px;
  padding-right: 36px;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}
.bookings-row td:first-child::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: var(--booking-card-accent, rgba(125, 125, 125, 0.5));
  box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.08);
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
  pointer-events: none;
}
.bookings-row td:last-child {
  padding-right: 22px;
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.bookings-row__time {
  font-size: clamp(11px, 0.95vw, 15px);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
}
.white-content .bookings-row__time {
  color: rgba(34, 42, 66, 0.95);
}
.bookings-row__event {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.bookings-row__customer {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  min-width: 0;
}
.bookings-row__customer-name-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}
.bookings-row__customer-contact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.bookings-row__inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bookings-row__collect-link {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  font-size: clamp(8px, 0.65vw, 10px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: #ff5d4d;
  background: rgba(236, 37, 13, 0.15);
  border: 1px solid rgba(236, 37, 13, 0.3);
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.bookings-row__collect-link:hover {
  color: #ff7a6d;
  background: rgba(236, 37, 13, 0.22);
  border-color: rgba(236, 37, 13, 0.5);
  text-decoration: none;
}

/* Mobile + tablet: compressed-row list pattern below 992px.
   Per UX brief: no card chrome, hairline dividers, fluid type via clamp(),
   compact actions, terse status pill. Inspired by Linear / Resy admin / OpenTable patterns. */
@media (max-width: 991.98px) {
  /* Strip the outer card's chrome on mobile so the bookings list uses the full
     viewport width. The inline `style="overflow-x: scroll"` on the card was a
     hidden cause of horizontal scroll within the card boundary. */
  .bookings-list-page .no-scrollbar.card,
  .bookings-list-page .card.no-scrollbar {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    overflow: visible !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .bookings-list-page .no-scrollbar.card > .card-body,
  .bookings-list-page .card.no-scrollbar > .card-body {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  /* Zero the col padding so the bookings card extends to the page's content-area
     edges, matching the full-bleed mobile nav. */
  .bookings-list-page [class*="col-"]:has(> .no-scrollbar.card) {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .bookings-table-wrap {
    overflow-x: visible;
    width: 100%;
    max-width: 100%;
  }
  /* Force the entire table out of table layout — table-layout uses content-based
     sizing that ignores min-width:0, so any over-wide cell content makes the
     whole table grow past its container. Block layout fixes this. */
  .bookings-table {
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    border-collapse: separate;
    border-spacing: 0;
  }
  .bookings-table colgroup,
  .bookings-table thead {
    display: none;
  }
  .bookings-day-header,
  .bookings-day-header td {
    display: block;
    width: 100%;
  }
  /* Stacked cards: the status cell flows inline, so give the booking-notice
     line its own row under the pill and undo the desktop column-fitting
     (negative margins + shrunken clamp). */
  .bookings-row .slot-notice {
    display: flex;
    width: 100%;
    margin: 4px 0 0;
    font-size: 10px;
    letter-spacing: 0.02em;
    white-space: normal;
  }
  .bookings-row .slot-notice i {
    font-size: 10px;
  }
  .bookings-table tbody {
    display: block;
    width: 100%;
    max-width: 100%;
  }
  /* === Compressed-row chrome: no card fill, just a hairline divider ===
     Row padding-left is the global content indent. Time is positioned absolutely
     in the left gutter, so EVERY secondary line — wherever it wraps — naturally
     starts at the row's content edge instead of needing per-cell padding-left. */
  .bookings-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    column-gap: 12px;
    row-gap: 6px;
    height: auto;
    padding: 14px 14px 14px 16px;
    margin: 0 0 8px 0;
    border-radius: 6px;
    /* Match desktop default row bg, not the hover color. */
    background: #2c2e48 !important;
    position: relative;
    box-shadow: none;
    /* Disable full-row clicks on mobile — explicit Edit button at the bottom
       opens the drawer instead. Children get pointer-events:auto so links,
       buttons, dropdowns inside the row still work. */
    cursor: default;
    pointer-events: none;
  }
  /* Available-slot rows are sparser (no customer/payments/participants); shrink
     vertical padding so the card feels intentional rather than hollow. */
  .bookings-row.bookings-row--available {
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .bookings-row.bookings-row--available > td:nth-child(7) > .booking-card__notes-actions {
    margin-top: 8px;
    padding-top: 8px;
  }
  .bookings-row * {
    pointer-events: auto;
  }
  /* Show the Edit button on mobile (overrides the desktop display:none) */
  .bookings-row .booking-card__edit-action {
    display: inline-flex !important;
  }
  .white-content .bookings-row {
    background: #ffffff !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.05);
  }
  .bookings-row:hover {
    background: #34365a !important;
  }
  .white-content .bookings-row:hover {
    background: #f5f6fa !important;
  }
  /* Round the accent strip to match the row's border-radius */
  .bookings-row::before {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  /* Reset all cells: no card-style label/value grid */
  .bookings-row td,
  .bookings-row td:first-child,
  .bookings-row td:last-child {
    display: flex;
    align-items: center;
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    min-width: 0;
  }
  /* Drop the data-label pseudo entirely (no more "TIME" / "STATUS" / etc.) */
  .bookings-row td::before {
    display: none !important;
  }
  /* Accent strip stays on the row's left edge (absolute, doesn't participate in flex flow) */
  .bookings-row::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--booking-card-accent, rgba(125, 125, 125, 0.5));
    pointer-events: none;
    z-index: 2;
  }
  /* Forced wrap after row 1 (time + status). flex: 0 0 100% on event alone
     isn't always reliable when the status pill is wide ("Waiver signed, no
     booking" et al.) — the pseudo guarantees event always starts a new line. */
  .bookings-row::after {
    content: '';
    display: block;
    flex: 0 0 100%;
    width: 100%;
    height: 0;
    order: 3;
    margin: 0;
    padding: 0;
  }

  /* === Line 1: STATUS (left) | TIME (right pushed via margin-left:auto) ===
     Mirrors the old card layout: status as the leading anchor, time top-right. */
  .bookings-row > td:nth-child(2) {
    order: 1;
    flex: 0 0 auto;
  }
  .bookings-row > td:nth-child(1) {
    order: 2;
    flex: 0 0 auto;
    position: static;
    display: flex;
    align-items: center;
    white-space: nowrap;
    margin-left: auto;
  }

  /* === Line 2: avatar + event name (after the wrap-forcer pseudo) === */
  .bookings-row > td:nth-child(3) {
    order: 4;
    flex: 0 0 100%;
    min-width: 0;
  }

  /* === Secondary lines (rows 3-7) — match the reference layout:
       Row 3: Transaction #X · Due: $Y · Paid: $Z   (inline)
       Row 4: Booked for N people     |   N signed   (two-column space-between)
       Row 5: Booked by Customer Name ↗   (one line)
       Row 6: 📧 email   📱 phone   (one line, inline)
       Row 7: Staff: N assigned   |   N/M available   (two-column space-between)
     All same size (14px), regular weight, dim color — uniform "secondary" tier. === */

  .bookings-row > td:nth-child(6) { order: 5; }  /* Payments */
  .bookings-row > td:nth-child(5) { order: 6; }  /* Participants */
  /* Customer cell — flex column. Same row-gap as .bookings-row for uniform
     vertical rhythm across all text lines on the card. */
  .bookings-row > td:nth-child(4) {
    order: 7;
    flex: 0 0 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  /* Staff row hidden on mobile — not part of the legacy card view.
     !important needed because later rules in the secondary-tier block apply
     `display: flex` to nth-child(8) at the same specificity. */
  .bookings-row > td:nth-child(8) { display: none !important; }

  /* Tier 3 (data): stats cells — Due/Paid/Booked-for/Checked-in
     12.5px / 400 / 0.85 alpha. Row-gap matches the row's row-gap (6px) so all
     text lines on the card have uniform vertical spacing. */
  .bookings-row > td:nth-child(5),
  .bookings-row > td:nth-child(6),
  .bookings-row > td:nth-child(8) {
    flex: 0 0 100%;
    font-size: 12.5px;
    line-height: 1.4;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    margin-top: 0;
    display: flex;
    align-items: baseline;
    gap: 6px 16px;
    flex-wrap: wrap;
    font-variant-numeric: tabular-nums;
  }
  /* Participants cell shrinks to match the "Booked by" / "Txn" prefix tier
     so "X participants" and "Y signed" sit at the same secondary scale
     as the rest of the card's data labels. Tight column gap so the slash
     separator reads as one continuous "X participants / Y signed" line. */
  .bookings-row > td:nth-child(5) {
    font-size: clamp(10px, 2.8vw, 12.5px);
    gap: 6px 4px;
  }
  /* Slash separator between "X participants" and "Y signed" on mobile
     (desktop renders them on separate lines so a slash would look odd). */
  .bookings-row > td:nth-child(5) .booking-card__stat-line--sub::before {
    content: '/ ';
    color: rgba(255, 255, 255, 0.5);
  }
  .white-content .bookings-row > td:nth-child(5) .booking-card__stat-line--sub::before {
    color: rgba(34, 42, 66, 0.5);
  }
  /* Payments cell — "Txn #X ↗", Due, Paid, Addons sit on one row.
     flex-wrap: nowrap forces a single line; clamp() shrinks font-size with
     viewport width so the line fits across mobile widths (320–430px).
     The "Txn" abbreviation saves enough horizontal space that no ellipsis
     or truncation is needed at any realistic phone width. */
  .bookings-row > td:nth-child(6) {
    flex-wrap: nowrap;
    min-width: 0;
    gap: clamp(4px, 1.4vw, 10px);
    align-items: center;
  }
  /* !important + double-class beats the downstream `display: inline`
     override that targets all stat-lines in cells 5/6/8. */
  .bookings-row > td:nth-child(6) > .booking-card__stat-line {
    display: inline-block !important;
    flex: 0 0 auto;
    white-space: nowrap;
  }
  .bookings-row > td:nth-child(6) > .booking-card__stat-line.booking-card__transaction-ref {
    display: inline-block !important;
    flex: 0 1 auto;
    min-width: 0;
    white-space: nowrap;
    font-size: clamp(10px, 2.8vw, 12.5px);
  }
  .bookings-row > td:nth-child(6) > .booking-card__stat-line:not(.booking-card__transaction-ref) {
    font-size: clamp(9px, 2.5vw, 11px);
  }
  .bookings-row > td:nth-child(6) > .booking-card__addons-chip {
    font-size: clamp(8px, 2vw, 9.5px);
    padding: 1px 5px;
    flex: 0 0 auto;
  }
  .white-content .bookings-row > td:nth-child(5),
  .white-content .bookings-row > td:nth-child(6),
  .white-content .bookings-row > td:nth-child(8) {
    color: rgba(34, 42, 66, 0.85);
  }

  /* Stat-lines inside cells: inline at the cell's font-size/color */
  .bookings-row > td:nth-child(5) .booking-card__stat-line,
  .bookings-row > td:nth-child(6) .booking-card__stat-line,
  .bookings-row > td:nth-child(8) .booking-card__stat-line {
    display: inline-flex;
    align-items: baseline;
    font-size: inherit;
    color: inherit;
    font-weight: 400;
    font-variant-numeric: tabular-nums;
  }

  /* All stat cells flow inline, left-aligned. */
  .bookings-row > td:nth-child(5),
  .bookings-row > td:nth-child(6),
  .bookings-row > td:nth-child(8) {
    justify-content: flex-start;
  }
  /* "Staff: " prefix on the first stat-line — same dim style as values, plain case */
  .bookings-row > td:nth-child(8) > .booking-card__stat-line:first-child::before {
    content: 'Staff: ';
    display: inline;
    color: inherit;
    font-weight: inherit;
  }

  /* Sub stat-lines (right-column values): same size + dim as primary for the
     reference's uniform-secondary look. No tier difference between left/right. */
  .bookings-row .booking-card__stat-line--sub {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-weight: 400;
  }
  .white-content .bookings-row .booking-card__stat-line--sub {
    color: rgba(34, 42, 66, 0.75);
  }
  /* Stat lines render inline (single horizontal line per cell) */
  .bookings-row > td:nth-child(5) .booking-card__stat-line,
  .bookings-row > td:nth-child(6) .booking-card__stat-line,
  .bookings-row > td:nth-child(8) .booking-card__stat-line {
    display: inline;
    font-size: inherit;
    color: inherit;
  }
  /* No dot separator — items are visually separated by the cell's column gap. */
  .bookings-row > td:nth-child(5) .booking-card__stat-line + .booking-card__stat-line::before,
  .bookings-row > td:nth-child(6) .booking-card__stat-line + .booking-card__stat-line::before,
  .bookings-row > td:nth-child(8) .booking-card__stat-line + .booking-card__stat-line::before {
    content: '';
    margin: 0;
  }
  /* Collect-link sits inline next to "X paid" */
  .bookings-row__collect-link {
    margin-top: 0;
    margin-left: 6px;
    padding: 1px 6px;
    font-size: 9px;
  }

  /* === Notes cell + bottom button row === */
  .bookings-row > td:nth-child(7) {
    order: 9;
    flex: 0 0 100%;
    margin-top: 0;
    flex-wrap: wrap;
    gap: 6px;
  }
  /* The note text container takes the full width — buttons drop below it. */
  .bookings-row > td:nth-child(7) > .booking-card__note {
    flex: 0 0 100%;
    width: 100%;
    max-width: 100%;
  }
  /* Bottom action row — separated from data above by margin only (no divider) */
  .bookings-row > td:nth-child(7) > .booking-card__notes-actions {
    flex: 0 0 100%;
    margin-top: 8px;
    padding-top: 0;
    border-top: none;
    justify-content: flex-start;
    gap: 6px;
  }
  .booking-card__notes-actions {
    margin-top: 0;
    gap: 4px;
    align-items: center;
  }

  /* === Inline action buttons (Book now / Add to cart) — full width below === */
  .bookings-row > td:has(> .bookings-row__inline-actions) {
    order: 99;
    flex: 0 0 100%;
    margin-top: 6px;
  }
  /* On available-slot rows (where action buttons exist), shrink the action cell
     to its content and put the notes cell inline beside it on the same line. */
  .bookings-row:has(> td > .bookings-row__inline-actions) > td:has(> .bookings-row__inline-actions) {
    flex: 0 0 auto;
    margin-top: 8px;
  }
  .bookings-row:has(> td > .bookings-row__inline-actions) > td:nth-child(7) {
    flex: 0 0 auto;
    order: 100;
    padding-left: 0;
    margin-top: 8px;
  }
  /* When inline with the action cell, the notes-actions doesn't need its own
     margin (its parent cell already has it). */
  .bookings-row:has(> td > .bookings-row__inline-actions) > td:nth-child(7) > .booking-card__notes-actions {
    margin-top: 0;
  }

  /* Hide cells that only contain an em-dash placeholder */
  .bookings-row > td:has(> .booking-card__cell-empty:only-child) {
    display: none;
  }

  /* === Event row: text-only on mobile (avatar hidden per user direction) === */
  .bookings-row__event {
    gap: 0;
    align-items: center;
  }
  .bookings-row__event .booking-avatar {
    display: none;
  }
  /* Event name — primary tier (UX: 15px/600 reads cleaner than 16-18/700 on dark) */
  .booking-card__game {
    font-size: 15px;
    line-height: 1.3;
    font-weight: 600;
    letter-spacing: -0.005em;
    color: rgba(255, 255, 255, 0.95);
  }
  .white-content .booking-card__game {
    color: rgba(34, 42, 66, 0.95);
  }
  .booking-card__event-meta {
    gap: 3px 6px;
  }
  .booking-card__schedule {
    font-size: clamp(0.75rem, 0.7rem + 0.15vw, 0.8125rem);
  }

  /* Time stamp — tabular nums for clean column alignment across rows */
  .booking-card__time-text {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: 0.02em;
    font-variant-numeric: tabular-nums;
    color: rgba(255, 255, 255, 0.85);
  }
  .white-content .booking-card__time-text {
    color: rgba(34, 42, 66, 0.85);
  }
  .white-content .booking-card__time-text {
    color: rgba(34, 42, 66, 0.95);
  }

  /* === Tiny status pill (UX brief: 18px height, 10px text, 6px h-pad) === */
  .booking-status-pill,
  .booking-status-pill--editable {
    padding: 1px 6px;
    font-size: clamp(0.625rem, 0.6rem + 0.15vw, 0.6875rem);
    min-height: 0;
    line-height: 1.5;
    border-radius: 3px;
    gap: 3px;
  }
  .booking-status-pill__caret {
    font-size: 9px;
  }

  /* === Customer line — matches reference:
         "Booked by Name ↗" on row 1
         📧 email   📱 phone (inline) on row 2 === */
  .bookings-row__customer {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    min-width: 0;
    max-width: 100%;
  }
  /* Customer-name (no link) sits at the secondary 0.85 tier with the rest of
     the card's data lines. Customer-LINK (clickable) is brand pink so it
     visually reads as interactive — the "Booked by " prefix is overridden
     back to white below so only the name itself is colored.
     Selectors prefixed with .bookings-row to beat the base
     .booking-card__customer-link rule (same specificity, later source). */
  .bookings-row .booking-card__customer-name {
    font-size: 13px;
    line-height: 1.4;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    min-width: 0;
    max-width: 100%;
    gap: 4px;
  }
  .bookings-row .booking-card__customer-link {
    font-size: 13px;
    line-height: 1.4;
    font-weight: 500;
    color: #e14eca;
    min-width: 0;
    max-width: 100%;
    gap: 4px;
  }
  .white-content .bookings-row .booking-card__customer-name {
    color: rgba(34, 42, 66, 0.85);
  }
  /* Customer link arrow — brand pink at slight reduced opacity */
  .bookings-row .booking-card__customer-link-icon {
    color: #e14eca;
    opacity: 0.85;
    font-size: 12px;
  }
  /* Tier 3 (data): email/phone — contact info is reference, not primary.
     No underline (per user direction). */
  .booking-card__customer-detail {
    font-size: 12.5px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.65);
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    text-decoration: none;
  }
  .white-content .booking-card__customer-detail {
    color: rgba(34, 42, 66, 0.7);
  }
  .booking-card__customer-detail > i {
    font-size: 12px;
    opacity: 0.65;
  }
  .bookings-row__customer-contact {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px 18px;
    min-width: 0;
    max-width: 100%;
  }
  /* Customer alert: keep the icon, hide the "Customer alert" text. !important
     needed because a later .bookings-row .booking-alert-chip rule has the same
     specificity and would otherwise win on source order. */
  .bookings-row__customer .booking-alert-chip {
    font-size: 0 !important;
    line-height: 1 !important;
    padding: 4px 5px !important;
    gap: 0 !important;
  }
  .bookings-row__customer .booking-alert-chip > i {
    font-size: 12px !important;
    margin: 0 !important;
    line-height: 1 !important;
  }
  /* "Booked by " prefix — small/dim label tier (matches "Txn" sizing on the
     payments line) so the customer NAME reads as the primary anchor.
     Explicit color override stops it inheriting pink from the link parent. */
  .bookings-row__customer .booking-card__customer-link::before,
  .bookings-row__customer .booking-card__customer-name::before {
    content: 'Booked by ';
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
    font-size: clamp(10px, 2.8vw, 12.5px);
    margin-right: 4px;
  }
  .white-content .bookings-row__customer .booking-card__customer-link::before,
  .white-content .bookings-row__customer .booking-card__customer-name::before {
    color: rgba(34, 42, 66, 0.85);
  }

  /* === Bottom-row buttons — hard-locked height + standardized icon box.
     Per UX brief: min-height lets icon-heavy buttons grow 1-2px, fixed height
     and fixed icon box prevent glyph-weight drift. */
  .bookings-row .booking-quick-action,
  .bookings-row .booking-quick-action--sm,
  .bookings-row .booking-card__notes-actions .booking-alert-chip--note,
  .bookings-row .booking-card__edit-action,
  .bookings-row .booking-card__collect-action {
    height: 30px !important;
    min-height: 30px !important;
    padding: 0 12px !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 1 !important;
    border-radius: 5px !important;
    letter-spacing: 0.01em !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
    box-sizing: border-box;
  }
  .bookings-row .booking-quick-action:hover {
    transform: none;
  }
  /* Icons in bottom-row buttons: fixed 13px box equalizes glyph widths. */
  .bookings-row .booking-quick-action > i,
  .bookings-row .booking-quick-action--sm > i,
  .bookings-row .booking-card__notes-actions .booking-alert-chip--note > i,
  .bookings-row .booking-card__edit-action > i,
  .bookings-row .booking-card__collect-action > i {
    font-size: 13px !important;
    line-height: 1 !important;
    width: 13px !important;
    text-align: center !important;
    display: inline-block !important;
    margin: 0 !important;
  }

  /* === Compact alert/note chips (Addons, Customer alert) === */
  .bookings-row .booking-alert-chip {
    padding: 2px 6px;
    font-size: clamp(0.5625rem, 0.55rem + 0.1vw, 0.625rem);
    border-radius: 3px;
    line-height: 1.3;
  }

  /* Notes count chip — sits inline next to the status pill in the header.
     Padding / line-height / font-size / border mirror .booking-status-pill
     exactly (incl. the 1px border that adds 2px to the pill's box model)
     so the two chips render at identical heights side-by-side. */
  .bookings-row .booking-card__notes-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 6px;
    font-size: clamp(0.625rem, 0.6rem + 0.15vw, 0.6875rem);
    font-weight: 500;
    line-height: 1.5;
    border: 1px solid rgba(125, 125, 125, 0.3);
    border-radius: 3px;
    background: rgba(125, 125, 125, 0.18);
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s ease, border-color 0.15s ease;
  }
  .bookings-row .booking-card__notes-chip:hover {
    background: rgba(125, 125, 125, 0.28);
    border-color: rgba(125, 125, 125, 0.5);
  }
  /* Icon uses el-icon-* family (same as pill's caret) so glyph metrics line
     up with the pill's existing icon at 9px. line-height: 1 + explicit
     font-size keeps it within the chip's text line-box (no cross-axis growth). */
  .bookings-row .booking-card__notes-chip-icon {
    font-size: 11px;
    line-height: 1;
    color: rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
  }
  .white-content .bookings-row .booking-card__notes-chip-icon {
    color: rgba(34, 42, 66, 0.7);
  }
  .white-content .bookings-row .booking-card__notes-chip {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(34, 42, 66, 0.85);
  }
  .white-content .bookings-row .booking-card__notes-chip:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  .white-content .bookings-row .booking-card__notes-chip > i {
    color: rgba(34, 42, 66, 0.7);
  }
  /* Status td becomes a flex row so pill + chip sit side-by-side cleanly. */
  .bookings-row > td:nth-child(2) {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Standalone note block hidden on mobile — the count chip is the affordance. */
  .bookings-row .booking-card__note {
    display: none !important;
  }
  /* "Add note" pill is sized to match the Book now / Add to cart button so
     it can sit inline next to them on available-slot rows. The 1px ghost
     border matches the chrome of Book now (which has a pink 1px border) so
     the two buttons read at the same visual height side-by-side — without
     the border, Add note's edges felt softer and the row looked misaligned. */
  .bookings-row .booking-alert-chip--note {
    padding: 5px 9px;
    font-size: clamp(0.6875rem, 0.65rem + 0.2vw, 0.75rem);
    border-radius: 5px;
    gap: 5px;
    line-height: 1.2;
    min-height: 28px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    border: 1px solid rgba(125, 125, 125, 0.3);
  }
  .white-content .bookings-row .booking-alert-chip--note {
    border-color: rgba(0, 0, 0, 0.15);
  }
  .bookings-row .booking-alert-chip--note > i {
    font-size: 10px;
    margin-right: 0 !important;
  }

  /* Long strings wrap at word boundaries; only break mid-word as a last resort */
  .booking-card__customer-detail,
  .booking-card__game {
    overflow-wrap: break-word;
    word-break: normal;
  }
  /* Notes truncate to a single line with ellipsis on mobile.
     The flex chain needs min-width:0 on every ancestor so flex items can shrink
     below their content size (default min-width:auto blocks shrinking). The text
     element is the ONLY place that uses overflow:hidden — required by spec for
     text-overflow:ellipsis to function (it has no effect on overflow:visible). */
  .bookings-row > td:nth-child(7) {
    min-width: 0;
    max-width: 100%;
  }
  .bookings-row .booking-card__note {
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
  }
  .bookings-row .booking-card__note-text {
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: clamp(0.75rem, 0.7rem + 0.2vw, 0.8125rem);
    line-height: 1.3;
  }

  /* Inline actions wrap nicely. align-items:center matches .booking-card__notes-actions
     so Book now baselines with Add note / Edit when the two cells sit side-by-side. */
  .bookings-row__inline-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    gap: 6px;
  }

  /* Workflow badge (event meta) — shrink to match new compact scale */
  .bookings-row .workflow-badge {
    margin-top: 0;
    padding: 2px 5px;
    font-size: 9px;
  }
}

/* === Check-in trigger (popover content styles in the non-scoped block at
   the bottom of this file — popper appends to body, scoped styles can't
   reach it). === */
.booking-card__checkin-trigger {
  cursor: pointer;
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  border-bottom: 1px dashed transparent;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.booking-card__checkin-trigger:hover {
  color: #e14eca;
  border-bottom-color: rgba(225, 78, 202, 0.4);
}
.booking-card__checkin-caret {
  font-size: 10px;
  opacity: 0.6;
  transition: opacity 0.15s ease;
}
.booking-card__checkin-trigger:hover .booking-card__checkin-caret {
  opacity: 1;
}
/* OTC-3117: signed waivers on an unbooked slot read as an alert; the caret shares the colour */
.booking-card__stat-line.booking-card__checkin-trigger--signed,
.booking-card__stat-line.booking-card__checkin-trigger--signed .booking-card__checkin-caret,
.white-content .booking-card__stat-line.booking-card__checkin-trigger--signed,
.white-content .booking-card__stat-line.booking-card__checkin-trigger--signed .booking-card__checkin-caret {
  color: #fb6340 !important;
  opacity: 1;
}

/* Smaller variant of the contextual quick action button */
.booking-quick-action--sm {
  padding: 4px 10px;
  font-size: 11px;
  border-radius: 6px;
  gap: 5px;
}
.booking-quick-action--sm i {
  font-size: 10px;
}
.booking-quick-action--neutral {
  color: rgba(255, 255, 255, 0.85);
  background: rgba(125, 125, 125, 0.12);
  border-color: rgba(125, 125, 125, 0.3);
}
.booking-quick-action--neutral:hover {
  background: rgba(125, 125, 125, 0.22);
  border-color: rgba(125, 125, 125, 0.5);
}
.white-content .booking-quick-action--neutral {
  color: rgba(34, 42, 66, 0.85);
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.15);
}
.white-content .booking-quick-action--neutral:hover {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.3);
}

.booking-card__cell {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}
.booking-card__cell--time {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}
.booking-card__time-allday {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.white-content .booking-card__time-allday {
  color: rgba(34, 42, 66, 0.7);
}
.booking-card__cell--status {
  align-items: flex-start;
}
.booking-card__cell--event {
  flex-direction: row;
  align-items: center;
  gap: 10px;
}
.booking-card__event-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.booking-card__event-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.booking-card__cell--customer {
  flex-wrap: wrap;
  font-size: 12px;
  align-content: center;
}
.booking-card__cell--customer > * {
  width: auto;
}
.booking-card__customer-alert {
  margin-top: 2px;
}
.booking-card__cell--stat {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
}
.booking-card__stat-line {
  font-size: clamp(11px, 0.85vw, 13px);
  color: rgba(255, 255, 255, 0.95);
}
.booking-card__stat-line--sub {
  font-size: clamp(9px, 0.72vw, 11px);
  color: rgba(255, 255, 255, 0.7);
}
.white-content .booking-card__stat-line {
  color: rgba(34, 42, 66, 0.95);
}
/* Desktop participants cell: both stat-lines (X participants, Y signed)
   render at the same font-size and color so they read as a matched pair.
   Each phrase is white-space:nowrap to stay whole; cell still allows wrap
   so the second line drops down cleanly when the column is narrow. */
@media (min-width: 992px) {
  .bookings-row > td:nth-child(5) .booking-card__stat-line {
    display: inline-block;
    white-space: nowrap;
  }
  .bookings-row > td:nth-child(5) .booking-card__stat-line--sub {
    font-size: clamp(11px, 0.85vw, 13px);
    color: rgba(255, 255, 255, 0.95);
  }
  .white-content .bookings-row > td:nth-child(5) .booking-card__stat-line--sub {
    color: rgba(34, 42, 66, 0.95);
  }
  .bookings-row > td:nth-child(5) .el-popover__reference-wrapper {
    display: inline-block;
  }

  /* Stack Due above Paid on desktop when both are visible (no Collect button).
     Mobile uses inline-block (rule at td:nth-child(6) > stat-line above) so
     this only takes effect on desktop. */
  .bookings-row > td:nth-child(6) .booking-card__amount--due,
  .bookings-row > td:nth-child(6) .booking-card__amount--paid {
    display: block;
  }
  /* Desktop payments cell: hide Due only when the Collect button is shown to
     replace it. If the user lacks payments_update the Collect link is gone,
     so keep Due visible. Paid is always visible. Mobile is untouched.
     This rule comes after the block-display rule above so its display:none
     wins for the collectable case (same specificity, later wins). */
  .bookings-row > td:nth-child(6) .booking-card__amount--due--collectable {
    display: none;
  }
  /* Addons chip moves into the event column on desktop. The cell-6 instance
     is hidden here; the event-column instance (.--event) becomes visible. */
  .bookings-row > td:nth-child(6) .booking-card__addons-chip {
    display: none;
  }
  .bookings-row .booking-card__addons-chip--event {
    display: inline-flex;
  }
}
/* By default, the event-column addons chip is hidden — only shown on
   desktop via the rule above. Keeps the mobile view unchanged (mobile uses
   the original cell-6 chip). */
.booking-card__addons-chip--event {
  display: none;
}
.booking-card__cell--notes {
  align-items: stretch;
}
.booking-card__notes-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.booking-card__notes-actions:first-child {
  margin-top: 0;
}
.booking-card__cell--action {
  align-items: flex-end;
}
.booking-card__cell-empty {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
}

/* Transaction ref line is mobile-only — desktop table doesn't have a column for it. */
.booking-card__transaction-ref {
  display: none;
}
/* Notes-count chip is mobile-only — desktop has its own Notes column. */
.booking-card__notes-chip {
  display: none;
}
/* Edit button is mobile-only — desktop has full-row click and the inline link
   icons in customer/transaction cells. */
.booking-card__edit-action {
  display: none !important;
}
/* Collect action chip is mobile-only; desktop keeps the inline `Collect $X due`
   text link in the payments cell. On mobile it sits in the action row as an
   amber-styled CTA so it reads like a button rather than passive text. */
.booking-card__collect-action {
  display: none !important;
}
@media (max-width: 991.98px) {
  /* Hide the inline collect text-link on mobile (replaced by the action chip below) */
  .bookings-row__collect-link {
    display: none !important;
  }
  /* Show the collect action chip on mobile — red, matching the original
     bookings-row__collect-link color treatment. */
  .bookings-row .booking-card__collect-action {
    display: inline-flex !important;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    min-height: 28px;
    border-radius: 6px;
    color: #ff5d4d !important;
    background: rgba(236, 37, 13, 0.15) !important;
    border: 1px solid rgba(236, 37, 13, 0.3) !important;
    text-transform: none;
    letter-spacing: 0;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  .bookings-row .booking-card__collect-action:hover {
    color: #ff7a6d !important;
    background: rgba(236, 37, 13, 0.22) !important;
    border-color: rgba(236, 37, 13, 0.5) !important;
    transform: none;
  }
  .bookings-row .booking-card__collect-action > i {
    font-size: 11px;
    margin-right: 0 !important;
  }
}
@media (max-width: 991.98px) {
  /* Transaction-ref flows inline with Due/Paid/Addons on the same row — same
     tier as the rest of the payments line (0.85). */
  .bookings-row .booking-card__transaction-ref {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.4;
  }
  .white-content .bookings-row .booking-card__transaction-ref {
    color: rgba(34, 42, 66, 0.85);
  }
  /* Transaction link — brand pink + 13px so the order number reads as the
     primary anchor on the line, matching the customer-link affordance.
     The "Txn" prefix outside the link stays at clamp(10–12.5px) (label tier). */
  .booking-card__transaction-ref-link {
    color: #e14eca;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 0.15s ease;
  }
  .booking-card__transaction-ref-link:hover {
    color: #e14eca;
    text-decoration: none;
    opacity: 0.8;
  }
  .booking-card__transaction-ref-link i {
    font-size: 12px;
    color: #e14eca;
    opacity: 0.85;
    transition: opacity 0.15s ease;
  }
  .booking-card__transaction-ref-link:hover i {
    opacity: 1;
  }
}
.white-content .booking-card__cell--time {
  color: rgba(34, 42, 66, 0.95);
}
.white-content .booking-card__cell--stat {
  color: rgba(34, 42, 66, 0.85);
}
.white-content .booking-card__stat-line--sub {
  color: rgba(34, 42, 66, 0.7);
}
.white-content .booking-card__cell-empty {
  color: rgba(34, 42, 66, 0.5);
}

@media (max-width: 1300px) {
  .bookings-header,
  .booking-card__body {
    overflow-x: auto;
  }
}
.booking-card__game {
  font-size: clamp(11px, 0.95vw, 15px);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}
.booking-card__chip {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.booking-card__schedule {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: clamp(9px, 0.72vw, 11px);
  color: rgba(255, 255, 255, 0.75);
}
.booking-card__schedule i {
  font-size: clamp(9px, 0.72vw, 11px);
  opacity: 0.85;
}
.white-content .booking-card__schedule {
  color: rgba(34, 42, 66, 0.7);
}
.booking-card__customer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}
.booking-card__customer-name {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
}
.booking-card__customer-link {
  color: #e14eca;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s ease;
}
.booking-card__customer-link:hover {
  color: #e14eca;
  text-decoration: underline;
}
.booking-card__customer-link-icon {
  font-size: 11px;
  color: #e14eca;
  opacity: 0.85;
  transition: opacity 0.15s ease;
}
.booking-card__customer-link:hover .booking-card__customer-link-icon {
  opacity: 1;
}
.booking-card__customer-detail {
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
}
.white-content .booking-card__customer-link {
  color: #e14eca;
}
.white-content .booking-card__customer-link:hover {
  color: #b03a9c;
}
.booking-card__stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}
.booking-card__stat-group {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-start;
  line-height: 1.3;
}
.booking-card__stat-group + .booking-card__stat-group {
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid rgba(125, 125, 125, 0.3);
}
.booking-card__stat-label {
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 1px;
}
.white-content .booking-card__stat-label {
  color: rgba(34, 42, 66, 0.5);
}
.booking-card__stat-values {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.booking-card__stat {
  white-space: nowrap;
}
.white-content .booking-card__stat-group + .booking-card__stat-group {
  border-left-color: rgba(0, 0, 0, 0.12);
}
.booking-card__alerts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

/* Important note preview — background hugs content, single-line truncated */
.booking-card__note-wrap {
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  max-width: 100%;
  min-width: 0;
}
.booking-card__add-note {
  flex-shrink: 0;
}
.booking-card__note {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  background: rgba(255, 200, 100, 0.08);
  border-left: 3px solid rgba(255, 180, 80, 0.6);
  border-radius: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
  cursor: pointer;
  transition: background 0.15s ease;
  max-width: 100%;
  min-width: 0;
}
.booking-card__note-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.booking-card__note-more {
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 200, 100, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.booking-card__note:hover {
  background: rgba(255, 200, 100, 0.15);
}
.booking-card__note:hover {
  background: rgba(255, 200, 100, 0.15);
}
.white-content .booking-card__note {
  background: rgba(255, 180, 80, 0.12);
  color: rgba(34, 42, 66, 0.85);
}
.white-content .booking-card__note:hover {
  background: rgba(255, 180, 80, 0.2);
}

/* Right rail: contextual quick action button */
.booking-card__rail {
  flex-shrink: 0;
  align-self: center;
  display: flex;
  align-items: center;
}
.booking-card__rail:empty {
  display: none;
}

.booking-quick-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
  border: 1px solid rgba(225, 78, 202, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  font-family: inherit;
  letter-spacing: 0.01em;
}
.booking-quick-action:hover {
  background: rgba(225, 78, 202, 0.16);
  border-color: rgba(225, 78, 202, 0.5);
  transform: translateX(-2px);
}
.booking-quick-action:active {
  transform: translateX(0);
}
.booking-quick-action__caret {
  font-size: 11px;
  opacity: 0.5;
  transition: transform 0.15s ease;
}
.booking-quick-action:hover .booking-quick-action__caret {
  transform: translateX(2px);
  opacity: 0.9;
}
.booking-quick-action--urgent {
  color: #ec250d;
  background: rgba(236, 37, 13, 0.08);
  border-color: rgba(236, 37, 13, 0.3);
}
.booking-quick-action--urgent:hover {
  background: rgba(236, 37, 13, 0.16);
  border-color: rgba(236, 37, 13, 0.5);
}

.white-content .booking-quick-action {
  background: rgba(225, 78, 202, 0.06);
  border-color: rgba(225, 78, 202, 0.25);
}
.white-content .booking-quick-action:hover {
  background: rgba(225, 78, 202, 0.14);
  border-color: rgba(225, 78, 202, 0.5);
}
.white-content .booking-quick-action--urgent {
  background: rgba(236, 37, 13, 0.06);
  border-color: rgba(236, 37, 13, 0.3);
}
.white-content .booking-quick-action--urgent:hover {
  background: rgba(236, 37, 13, 0.14);
  border-color: rgba(236, 37, 13, 0.5);
}

/* Alert / addon / note chips on the card */
.booking-alert-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: rgba(225, 78, 202, 0.15);
  color: #e14eca;
  cursor: pointer;
  transition: background 0.15s ease;
}
.booking-alert-chip:hover {
  background: rgba(225, 78, 202, 0.25);
}
.booking-alert-chip--neutral {
  background: rgba(125, 125, 125, 0.18);
  color: rgba(255, 255, 255, 0.85);
}
.booking-alert-chip--neutral:hover {
  background: rgba(125, 125, 125, 0.28);
}
.booking-alert-chip--note {
  background: rgba(125, 125, 125, 0.12);
  color: rgba(255, 255, 255, 0.7);
  cursor: default;
  text-transform: none;
  letter-spacing: 0;
}
.booking-alert-chip--note:hover {
  background: rgba(125, 125, 125, 0.12);
}
/* Customer-alert variant: amber to match the alert surface inside the
   BookingDrawer so the same content carries the same hue end-to-end. */
.booking-alert-chip--alert {
  background: rgba(255, 214, 0, 0.15);
  color: #ffd600;
  border: 1px solid rgba(255, 214, 0, 0.4);
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.booking-alert-chip--alert:hover {
  background: rgba(255, 214, 0, 0.25);
  border-color: rgba(255, 214, 0, 0.6);
  color: #ffea4d;
}

/* Light-mode booking card */
.white-content .booking-card {
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.white-content .booking-card:hover {
  background: rgba(0, 0, 0, 0.02);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.white-content .booking-card__time-text,
.white-content .booking-card__game,
.white-content .booking-card__customer-name {
  color: rgba(34, 42, 66, 0.95);
}
.white-content .booking-card__customer-detail,
.white-content .booking-card__stats {
  color: rgba(34, 42, 66, 0.7);
}
.white-content .booking-alert-chip--neutral {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(34, 42, 66, 0.85);
}
.white-content .booking-alert-chip--note {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(34, 42, 66, 0.65);
}

/* ─── Bookings table light-mode polish ─── */
/* B7: rows need a visible boundary on white (matches .cf-card pattern) */
.white-content .bookings-row {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.05);
}
.white-content .bookings-row:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
}
/* B5: status pill colors come from the status settings via inline style (background + text).
   Default 'available' is a white chip, so give it a visible edge on the white card. */
.white-content .booking-status-pill--available {
  border-color: rgba(30, 30, 47, 0.35);
}
/* Available row's accent strip (CSS var --booking-card-accent) is white in dark mode —
   override to black on light mode so the row identity is visible against the white card.
   !important needed because the row sets the var via inline :style (1000 specificity). */
.white-content .bookings-row--available {
  --booking-card-accent: #1e1e2f !important;
}
/* B1: text-warning ("Incomplete" customer state) Bootstrap default is unreadable on white */
.white-content .bookings-row .booking-card__customer-name.text-warning {
  color: #b87800;
}
/* B2: badge-danger "In cart" — soften the saturated red on white */
.white-content .bookings-row .badge-danger {
  background-color: rgba(236, 37, 13, 0.1) !important;
  color: #c91d05 !important;
  border: 1px solid rgba(236, 37, 13, 0.25);
}
/* B3: important-note pill needs definition on white card */
.white-content .booking-card__note {
  border: 1px solid rgba(214, 143, 0, 0.3);
  border-left-width: 3px;
  border-left-color: rgba(214, 143, 0, 0.7);
}
.white-content .booking-card__note-more {
  color: rgba(180, 120, 0, 0.85);
}
/* B4: Collect Due pill needs darker red on white for AA contrast */
.white-content .bookings-row__collect-link {
  color: #c91d05;
  background: rgba(236, 37, 13, 0.08);
  border-color: rgba(236, 37, 13, 0.4);
}
.white-content .bookings-row__collect-link:hover {
  color: #a8170a;
  background: rgba(236, 37, 13, 0.16);
  border-color: rgba(236, 37, 13, 0.6);
}
/* Customer-alert chip light-mode: darker amber for AA contrast on white,
   matching the .booking-card__note-more darker-amber pattern above. */
.white-content .booking-alert-chip--alert {
  color: #8a6a00;
  background: rgba(180, 120, 0, 0.08);
  border-color: rgba(180, 120, 0, 0.4);
}
.white-content .booking-alert-chip--alert:hover {
  color: #6b5200;
  background: rgba(180, 120, 0, 0.16);
  border-color: rgba(180, 120, 0, 0.6);
}
/* B6: workflow-badge inline color is tuned for dark; darken+saturate in light */
.white-content .workflow-badge {
  filter: brightness(0.7) saturate(1.25);
}
/* B8: customer-link external-arrow icon was nearly invisible at rest in light mode */
.white-content .booking-card__customer-link-icon {
  opacity: 0.7;
}
/* booking-card__stat-line + stat-line--sub readable on white */
.white-content .booking-card__stat-line {
  color: rgba(34, 42, 66, 0.95);
}
.white-content .booking-card__stat-line--sub {
  color: rgba(34, 42, 66, 0.65);
}
/* booking-card__cell-empty placeholder readable on white */
.white-content .booking-card__cell-empty {
  color: rgba(34, 42, 66, 0.4);
}
/* Schedule meta + workflow secondary text on white */
.white-content .booking-card__schedule {
  color: rgba(34, 42, 66, 0.65);
}

/* Mobile: shrink the time column, stack alerts below */
@media (max-width: 575px) {
  .bookings-row {
    padding-top: 9px;
    padding-right: 10px;
    padding-bottom: 9px;
    column-gap: 6px;
  }
  /* Hide schedule meta on the tightest screens — event name + status carry the line */
  .bookings-row .booking-card__schedule {
    display: none;
  }
}

/* Phone toolbar range (below 992px, same as BookingMobileToolbar; OTC-2955) */
@media (max-width: 991.98px) {
  /* === Mobile header tidy-up ===
     The d-lg-none mobile block stacks view-switcher / gear / cart / date-picker /
     Create-new with inline margin-top: 16px on each, giving wasted vertical space.
     Tighten margins, shrink the date-picker and Create-new button to a sane height.
     Keep Bootstrap's default .col-12 padding (15px) so the nav sits at the same
     horizontal inset as the bookings rows below. */
  .bookings-list-page .row.d-lg-none > .col-12 {
    margin-bottom: 8px;
  }
  .bookings-list-page .row.d-lg-none > .col-12:last-child {
    margin-bottom: 0;
  }
  .bookings-list-page .row.d-lg-none .view-switcher,
  .bookings-list-page .row.d-lg-none .gear-menu,
  .bookings-list-page .row.d-lg-none > .col-12 > .base-button,
  .bookings-list-page .row.d-lg-none > .col-12 > button {
    margin-top: 0 !important;
  }
  .bookings-list-page .row.d-lg-none .base-input.mt-3 {
    margin-top: 0 !important;
    margin-bottom: 0;
  }
  /* Date picker — shorter, less padded */
  .bookings-list-page .row.d-lg-none .el-date-editor {
    height: 38px;
    line-height: 38px;
    width: 100%;
  }
  .bookings-list-page .row.d-lg-none .el-date-editor .el-input__inner {
    height: 38px;
    line-height: 38px;
    padding-top: 0;
    padding-bottom: 0;
  }
  /* Create new button — match other controls' height (was towering ~70px) */
  .bookings-list-page .row.d-lg-none .dropdown-toggle.btn-primary.btn-block {
    height: 42px;
    padding: 8px 16px;
    font-size: 14px;
    line-height: 1.4;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Light-mode override for the booking list cells */
.white-content .bookings-list-page ::v-deep .b-table .text-white {
  color: rgba(34, 42, 66, 0.85) !important;
}

.colon-field{
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  margin-top: 0.5rem;
}

img {
  width: 60px;
  height: 60px;
  -webkit-border-radius: 60px;
  -webkit-background-clip: padding-box;
  -moz-border-radius: 60px;
  -moz-background-clip: padding;
  border-radius: 60px;
  background-clip: padding-box;
  margin: 7px 0 0 5px;
  float: left;
  background-size: cover;
  background-position: center center;
  margin-top: -7px;
  margin-bottom: -7px;
}


.closed{
  position: absolute;
  pointer-events: all;
  z-index:10;
  right: 15px;
  right: 15px;
  top: 50%;
  margin-top: -13px;
  width: 25px;
  height: 25px;
  padding: 3px;
}
.closed:hover {
  color: #560606 !important;
}

.mark-information sup {
  background-color: rgb(153, 173, 207) !important;
}

.workflow-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 6px;
  padding: 4px 7px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  border: none;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.workflow-badge:hover {
  opacity: 0.85;
}

.workflow-dropdown-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  vertical-align: middle;
}

.view-switcher {
  display: inline-flex;
  background: rgba(225, 78, 202, 0.15);
  border-radius: 8px;
  padding: 4px;
  height: 38px;
  align-items: center;
}

.view-switcher__btn {
  width: 36px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.view-switcher__btn:hover {
  color: #e14eca;
}

.view-switcher__btn--active {
  background: rgba(225, 78, 202, 0.4);
  color: #e14eca;
}

.view-switcher__btn svg {
  width: 18px;
  height: 18px;
}

/* Light mode view-switcher styles - scoped to this page */
.white-content .bookings-list-page .view-switcher {
  background: rgba(225, 78, 202, 0.15);
}

.white-content .bookings-list-page .view-switcher__btn {
  color: rgba(225, 78, 202, 0.6);
}

.white-content .bookings-list-page .view-switcher__btn:hover {
  color: #e14eca;
}

.white-content .bookings-list-page .view-switcher__btn--active {
  background: rgba(225, 78, 202, 0.4);
  color: #e14eca;
}

</style>

<style>
/* Waiver popover (anchored to "X signed" trigger in the bookings list).
   Placed in the non-scoped block because el-popover appends the popper to
   <body>, where scoped data attributes don't reach. */
.booking-card__waivers-popover.el-popover {
  padding: 6px 0;
  min-width: 220px;
  max-width: 320px;
  background: #2c2e48;
  border: 1px solid rgba(225, 78, 202, 0.25);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
.white-content .booking-card__waivers-popover.el-popover {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.booking-card__waivers-list {
  display: flex;
  flex-direction: column;
}
.booking-card__waivers-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px;
}
.booking-card__waivers-item + .booking-card__waivers-item {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.white-content .booking-card__waivers-item + .booking-card__waivers-item {
  border-top-color: rgba(0, 0, 0, 0.06);
}
.booking-card__waivers-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #e14eca;
  text-decoration: none;
  transition: opacity 0.15s ease;
}
.booking-card__waivers-name:hover {
  color: #e14eca;
  opacity: 0.8;
  text-decoration: none;
}
.booking-card__waivers-name > i {
  font-size: 11px;
  opacity: 0.85;
}
.booking-card__waivers-name-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.booking-card__waivers-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1.4;
  white-space: nowrap;
}
.booking-card__waivers-tag--minor {
  background: rgba(0, 188, 212, 0.18);
  color: #4dd0e1;
  border: 1px solid rgba(0, 188, 212, 0.4);
}
.booking-card__waivers-tag--not-participating {
  background: rgba(125, 125, 125, 0.18);
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(125, 125, 125, 0.4);
}
.white-content .booking-card__waivers-tag--minor {
  background: rgba(0, 150, 175, 0.12);
  color: #00838f;
  border-color: rgba(0, 150, 175, 0.35);
}
.white-content .booking-card__waivers-tag--not-participating {
  background: rgba(34, 42, 66, 0.08);
  color: rgba(34, 42, 66, 0.7);
  border-color: rgba(34, 42, 66, 0.2);
}
.booking-card__waivers-email {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.15s ease;
}
.booking-card__waivers-email:hover {
  color: #e14eca;
  text-decoration: none;
}
.booking-card__waivers-email > i {
  font-size: 11px;
  opacity: 0.7;
}
.white-content .booking-card__waivers-email {
  color: rgba(34, 42, 66, 0.7);
}
.booking-card__waivers-empty {
  padding: 12px 14px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
}
.white-content .booking-card__waivers-empty {
  color: rgba(34, 42, 66, 0.6);
}

.waiver-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.waiver-option {
  border: 1px solid #2b3553;
  border-radius: 8px;
  padding: 12px 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.waiver-option:hover {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.02);
}
.waiver-option.selected {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.03);
}
.waiver-option .el-radio {
  margin-right: 0;
}
.waiver-option .el-radio__label {
  color: #fff;
  font-weight: 500;
}
.waiver-option.selected .el-radio__label {
  color: #e14eca;
}
.waiver-option .el-radio__input.is-checked .el-radio__inner {
  border-color: #e14eca;
  background: #e14eca;
}
.waiver-option small {
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
  line-height: 1.4;
  padding-left: 24px;
}
</style>
